/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AirplaneTilt,
  ArrowLeft,
  ArrowsClockwise,
  Baby,
  Barbell,
  BookOpen,
  BookmarkSimple,
  Briefcase,
  Calendar,
  Camera,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  Chat,
  ChatCircle,
  Check,
  Clock,
  Compass,
  Cpu,
  Dog,
  Eye,
  FilmStrip,
  Fire,
  Flag,
  ForkKnife,
  Funnel,
  GameController,
  Gear,
  Globe,
  GraduationCap,
  Handshake,
  Heart,
  House,
  Lightning,
  MagnifyingGlass,
  MapPin,
  Microphone,
  Mountains,
  MusicNote,
  NavigationArrow,
  Palette,
  PaperPlaneTilt,
  Plus,
  ShareNetwork,
  Shield,
  ShoppingBag,
  SignOut,
  Sparkle,
  Star,
  Tag,
  ThumbsUp,
  Ticket,
  TrendUp,
  UploadSimple,
  User,
  Users,
  WarningCircle,
  X
} from '@phosphor-icons/react';

// Australian cities/suburbs for explore map autocomplete
import AU_PLACES from './auPlaces';

// ============================================
// CONFIG
// ============================================
import { API_URL } from './apiConfig';
import NarbodicaAuth from './narb-auth/src/components/NarbodicaAuth.jsx';
import NarbodicaProfileOnboarding from './narb-auth/src/onboarding/NarbodicaProfileOnboarding.jsx';
import FeedbackButton from './components/FeedbackButton.jsx';
import IdentityGate from './components/IdentityGate.jsx';
import CreateEvent from './components/CreateEvent.jsx';
import TrySomethingNew from './components/TrySomethingNew.jsx';
import GroupsView from './components/GroupsView.jsx';
import GroupChat from './components/GroupChat.jsx';
import AccessibilitySettings from './components/AccessibilitySettings.jsx';
import { AccessibilityToggle, useAccessibilityStore, detectAccessibilityNeeds } from './narb-auth/src/accessibility';
import { loadAndApplyPreferences } from './utils/preferencesLoader.js';
import { API_BASE } from './apiConfig';

// ============================================
// INTERESTS TAXONOMY (17 groups, 250+ interests)
// ============================================
const INTERESTS_TAXONOMY = {
  physical_sports: {
    id: 'physical_sports', label: 'Physical & Sports', icon: Barbell, color: '#4ade80', bg: 'rgba(74,222,128,0.15)',
    interests: ['Running', 'Gym & Weightlifting', 'Yoga', 'Pilates', 'Swimming', 'Cycling', 'Hiking', 'Rock Climbing', 'Surfing', 'Martial Arts', 'Boxing', 'Tennis', 'Golf', 'Basketball', 'AFL', 'NRL & Rugby League', 'Rugby Union', 'Touch Football', 'Soccer', 'Cricket', 'Volleyball', 'Skateboarding', 'Dancing', 'Sailing', 'Fishing', 'Scuba Diving', 'Kayaking', 'Horse Riding', 'Parkour', 'Archery', 'Badminton', 'Table Tennis', 'Triathlon', 'Pickleball', 'Netball', 'Hockey', 'Baseball', 'Softball', 'Squash', 'Mountain Biking', 'BMX', 'CrossFit', 'Powerlifting', 'BJJ', 'Judo', 'Wrestling', 'Wakeboarding', 'Kitesurfing', 'Paddleboarding', 'Rowing', 'Water Polo']
  },
  music: {
    id: 'music', label: 'Music', icon: MusicNote, color: '#a78bfa', bg: 'rgba(167,139,250,0.15)',
    interests: ['Live Music', 'Concerts', 'Festivals', 'DJing', 'Singing & Karaoke', 'Guitar', 'Piano & Keyboards', 'Drums', 'Music Production', 'Classical Music', 'Jazz', 'Hip Hop', 'Electronic', 'Rock', 'Indie', 'Country', 'R&B & Soul', 'Metal', 'Reggae', 'Latin Music', 'K-Pop', 'Choir & A Cappella', 'Vinyl & Record Collecting', 'Folk & Acoustic']
  },
  food_drink: {
    id: 'food_drink', label: 'Food & Drink', icon: ForkKnife, color: '#fb923c', bg: 'rgba(251,146,60,0.15)',
    interests: ['Cooking', 'Baking', 'BBQ & Grilling', 'Wine Tasting', 'Craft Beer', 'Cocktails', 'Coffee Culture', 'Foodie & Restaurant Exploring', 'Vegan & Plant-Based', 'Meal Prep', 'Fermentation', 'Farmers Markets', 'Food Trucks', 'Tea', 'Chocolate', 'Cheese', 'Sushi', 'Street Food', 'Brunch Culture']
  },
  arts_creative: {
    id: 'arts_creative', label: 'Arts & Creative', icon: Palette, color: '#f472b6', bg: 'rgba(244,114,182,0.15)',
    interests: ['Painting', 'Drawing', 'Photography', 'Pottery & Ceramics', 'Sculpture', 'Printmaking', 'Digital Art', 'Graphic Design', 'Calligraphy', 'Jewellery Making', 'Knitting & Crochet', 'Sewing', 'Woodworking', 'Leathercraft', 'Candle Making', 'Flower Arranging', 'Tattoo Art', 'Graffiti & Street Art', 'Film Making', 'Animation', 'Theatre & Performing Arts']
  },
  technology: {
    id: 'technology', label: 'Technology', icon: Cpu, color: '#38bdf8', bg: 'rgba(56,189,248,0.15)',
    interests: ['Programming & Coding', 'AI & Machine Learning', 'Web Development', 'App Development', 'Cybersecurity', 'Robotics', '3D Printing', 'VR & AR', 'Blockchain & Crypto', 'Data Science', 'Game Development', 'Open Source', 'Smart Home & Automation', 'Drones', 'Electronics & Arduino', 'UX Design']
  },
  gaming: {
    id: 'gaming', label: 'Gaming', icon: GameController, color: '#818cf8', bg: 'rgba(129,140,248,0.15)',
    interests: ['Video Games', 'Board Games', 'Card Games', 'Tabletop RPGs', 'Poker', 'Chess', 'Puzzles & Escape Rooms', 'Trivia', 'Retro Gaming', 'Esports', 'Game Streaming', 'Cosplay', 'LARPing', 'Warhammer & Miniatures']
  },
  outdoors_nature: {
    id: 'outdoors_nature', label: 'Outdoors & Nature', icon: Mountains, color: '#34d399', bg: 'rgba(52,211,153,0.15)',
    interests: ['Camping', 'Bushwalking', 'Bird Watching', 'Gardening', 'Stargazing & Astronomy', 'Beach & Ocean', 'National Parks', 'Foraging', 'Conservation & Volunteering', 'Wildlife Photography', 'Rock Pooling', 'Mountain Biking', 'Trail Running', 'Four-Wheel Driving', 'Canoeing']
  },
  reading_writing: {
    id: 'reading_writing', label: 'Reading & Writing', icon: BookOpen, color: '#fbbf24', bg: 'rgba(251,191,36,0.15)',
    interests: ['Book Clubs', 'Fiction', 'Non-Fiction', 'Sci-Fi & Fantasy', 'True Crime', 'Poetry', 'Journaling', 'Creative Writing', 'Blogging', 'Comics & Manga', 'Audiobooks', 'Philosophy', 'History Books', 'Self-Help & Personal Development', 'Screenwriting']
  },
  film_tv: {
    id: 'film_tv', label: 'Film & TV', icon: FilmStrip, color: '#c084fc', bg: 'rgba(192,132,252,0.15)',
    interests: ['Movies', 'Documentaries', 'Anime', 'Horror', 'Sci-Fi', 'Comedy', 'Indie Film', 'Film Festivals', 'Home Cinema', 'Movie Marathons', 'TV Series & Binging', 'Film Photography', 'Movie Trivia', 'Stand-Up Comedy']
  },
  learning_education: {
    id: 'learning_education', label: 'Learning & Education', icon: GraduationCap, color: '#22d3ee', bg: 'rgba(34,211,238,0.15)',
    interests: ['Languages', 'History', 'Science', 'Psychology', 'Economics', 'Public Speaking', 'Debating', 'Workshops', 'Online Courses', 'Mentoring', 'Teaching', 'TED Talks', 'Lectures', 'Museums', 'Podcasts']
  },
  social_community: {
    id: 'social_community', label: 'Social & Community', icon: Users, color: '#fb7185', bg: 'rgba(251,113,133,0.15)',
    interests: ['Volunteering', 'Charity Work', 'Activism', 'Politics', 'Environmental Causes', 'Animal Welfare', 'Mentoring', 'Networking', 'Community Organising', 'Cultural Events', 'Festivals', 'Markets', 'Meetups']
  },
  wellbeing_mindfulness: {
    id: 'wellbeing_mindfulness', label: 'Wellbeing & Mindfulness', icon: Handshake, color: '#2dd4bf', bg: 'rgba(45,212,191,0.15)',
    interests: ['Meditation', 'Breathwork', 'Journaling', 'Mental Health Awareness', 'Sound Healing', 'Retreats', 'Spa & Self-Care', 'Aromatherapy', 'Acupuncture', 'Nutrition', 'Sleep Optimisation', 'Cold Water Therapy', 'Mindful Walking']
  },
  travel_exploration: {
    id: 'travel_exploration', label: 'Travel & Exploration', icon: AirplaneTilt, color: '#60a5fa', bg: 'rgba(96,165,250,0.15)',
    interests: ['Road Trips', 'Backpacking', 'Solo Travel', 'Luxury Travel', 'Cultural Travel', 'Van Life', 'Hostels', 'City Breaks', 'Adventure Travel', 'Island Hopping', 'Train Travel', 'Travel Photography', 'Language Immersion', 'Festivals Abroad', 'House Sitting']
  },
  business_career: {
    id: 'business_career', label: 'Business & Career', icon: Briefcase, color: '#94a3b8', bg: 'rgba(148,163,184,0.15)',
    interests: ['Entrepreneurship', 'Startups', 'Investing', 'Real Estate', 'Marketing', 'Side Hustles', 'Freelancing', 'Leadership', 'Productivity', 'Finance & Budgeting', 'Networking', 'Career Coaching', 'E-Commerce', 'Content Creation']
  },
  pets_animals: {
    id: 'pets_animals', label: 'Pets & Animals', icon: Dog, color: '#a3e635', bg: 'rgba(163,230,53,0.15)',
    interests: ['Dogs', 'Cats', 'Aquariums & Fish', 'Birds', 'Reptiles', 'Horse Riding', 'Pet Training', 'Animal Rescue', 'Wildlife', 'Veterinary Interest', 'Pet Photography']
  },
  family_lifestyle: {
    id: 'family_lifestyle', label: 'Family & Lifestyle', icon: House, color: '#f0abfc', bg: 'rgba(240,171,252,0.15)',
    interests: ['Parenting', 'Kids Activities', 'Home Decorating & Interior Design', 'DIY & Home Improvement', 'Thrifting & Op Shopping', 'Sustainability & Zero Waste', 'Minimalism', 'Vintage & Antiques', 'Fashion & Style', 'Sneakers', 'Cars & Automotive', 'Motorcycles', 'Motorsport']
  },
  spiritual_cultural: {
    id: 'spiritual_cultural', label: 'Spiritual & Cultural', icon: Sparkle, color: '#fde047', bg: 'rgba(253,224,71,0.15)',
    interests: ['Religion & Faith', 'Astrology', 'Tarot', 'Indigenous Culture', 'Cultural Heritage', 'Philosophy', 'Mythology', 'Ceremony & Ritual']
  }
};

// ============================================
// CATEGORY ICONS & COLORS
// ============================================
const CATEGORY_CONFIG = {
  'Music & Concerts': { icon: MusicNote, color: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
  'Sports & Fitness': { icon: Barbell, color: '#4ade80', bg: 'rgba(74,222,128,0.15)' },
  'Food & Drink': { icon: ForkKnife, color: '#fb923c', bg: 'rgba(251,146,60,0.15)' },
  'Arts & Culture': { icon: Palette, color: '#f472b6', bg: 'rgba(244,114,182,0.15)' },
  'Technology & Innovation': { icon: Cpu, color: '#38bdf8', bg: 'rgba(56,189,248,0.15)' },
  'Business & Networking': { icon: Briefcase, color: '#94a3b8', bg: 'rgba(148,163,184,0.15)' },
  'Education & Workshops': { icon: BookOpen, color: '#fbbf24', bg: 'rgba(251,191,36,0.15)' },
  'Outdoor & Adventure': { icon: Mountains, color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
  'Gaming & Esports': { icon: GameController, color: '#818cf8', bg: 'rgba(129,140,248,0.15)' },
  'Community & Social': { icon: Users, color: '#fb7185', bg: 'rgba(251,113,133,0.15)' },
  'Health & Wellness': { icon: Handshake, color: '#2dd4bf', bg: 'rgba(45,212,191,0.15)' },
  'Pets & Animals': { icon: Dog, color: '#a3e635', bg: 'rgba(163,230,53,0.15)' },
  'Travel & Experiences': { icon: AirplaneTilt, color: '#22d3ee', bg: 'rgba(34,211,238,0.15)' },
  'Film & Media': { icon: FilmStrip, color: '#c084fc', bg: 'rgba(192,132,252,0.15)' },
  'Comedy & Entertainment': { icon: Microphone, color: '#fde047', bg: 'rgba(253,224,71,0.15)' },
  'Markets & Shopping': { icon: ShoppingBag, color: '#f0abfc', bg: 'rgba(240,171,252,0.15)' },
  'Family & Kids': { icon: Baby, color: '#93c5fd', bg: 'rgba(147,197,253,0.15)' },
  'Charity & Volunteering': { icon: Heart, color: '#fca5a5', bg: 'rgba(252,165,165,0.15)' },
  'Other': { icon: Tag, color: '#a1a1aa', bg: 'rgba(161,161,170,0.15)' }
};

function getCategoryConfig(category) {
  return CATEGORY_CONFIG[category] || CATEGORY_CONFIG['Other'];
}

// ============================================
// HELPERS
// ============================================
function formatDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatPrice(pricing) {
  if (pricing?.isFree) return 'Free';
  if (pricing?.price) return `$${pricing.price}`;
  return 'Free';
}

function timeUntil(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = d - now;
  if (diff < 0) return 'Started'; // Already started
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `In ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `In ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 7) return `In ${days} days`;
  if (days < 30) return `In ${Math.floor(days / 7)} weeks`;
  return `In ${Math.floor(days / 30)} months`;
}

// ============================================
// SPARK → CONNECT INTEREST MAPPER
// ============================================
// Maps Spark's free-form interest tags to Connect's INTERESTS_TAXONOMY entries
function mapSparkTagsToTaxonomy(tags) {
  if (!tags || tags.length === 0) return { interests: [], interestGroups: [] };

  const MANUAL_MAP = {
    'singing': 'Singing & Karaoke', 'birdwatching': 'Bird Watching',
    'horseback riding': 'Horse Riding', 'diy projects': 'DIY & Home Improvement',
    'jewelry making': 'Jewellery Making', 'vintage items': 'Vintage & Antiques',
    'foodie adventures': 'Foodie & Restaurant Exploring', 'vegan': 'Vegan & Plant-Based',
    'pc gaming': 'Video Games', 'photography tech': 'Photography',
    'live comedy': 'Stand-Up Comedy', 'stand-up comedy': 'Stand-Up Comedy',
    'career development': 'Career Coaching', 'astronomy': 'Stargazing & Astronomy',
    'wildlife': 'Wildlife Photography',
  };

  const allEntries = [];
  Object.entries(INTERESTS_TAXONOMY).forEach(([gId, g]) =>
    g.interests.forEach(i => allEntries.push({ interest: i, groupId: gId, lower: i.toLowerCase() }))
  );

  const matched = new Set();
  const groups = new Set();

  tags.forEach(tag => {
    const lower = (typeof tag === 'string' ? tag : '').toLowerCase().trim();
    if (!lower) return;
    // 1. Manual map
    if (MANUAL_MAP[lower]) {
      const entry = allEntries.find(e => e.interest === MANUAL_MAP[lower]);
      if (entry) { matched.add(entry.interest); groups.add(entry.groupId); return; }
    }
    // 2. Exact match (case-insensitive)
    let entry = allEntries.find(e => e.lower === lower);
    if (entry) { matched.add(entry.interest); groups.add(entry.groupId); return; }
    // 3. Contains match (only if tag is 4+ chars to avoid false positives)
    if (lower.length >= 4) {
      entry = allEntries.find(e => e.lower.includes(lower) || lower.includes(e.lower));
      if (entry) { matched.add(entry.interest); groups.add(entry.groupId); }
    }
  });

  return { interests: [...matched], interestGroups: [...groups] };
}

// ============================================
// API HELPER
// ============================================
async function api(endpoint, options = {}) {
  const token = localStorage.getItem('connect_token');
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    },
    ...options
  };
  const res = await fetch(`${API_URL}${endpoint}`, config);
  let data;
  try {
    data = await res.json();
  } catch {
    throw new Error(res.status === 0 ? 'Network error — check your connection' : `Server error (${res.status})`);
  }
  if (!res.ok) {
    // Extract the most useful error message
    // Backend often returns { message: "Registration failed", error: "User validation failed: email: Please enter a valid email" }
    // We want the specific validation error, not the generic wrapper
    let msg = '';

    // 1. Check for validation errors array first (most specific)
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      msg = data.errors.map(e => e.msg || e.message || e).join('. ');
    }
    // 2. Check data.error for validation details (e.g. "User validation failed: email: Please enter a valid email")
    else if (data.error && typeof data.error === 'string') {
      // Extract the readable part after the last colon for mongoose validation errors
      const validationMatch = data.error.match(/:\s*([^:]+)$/);
      msg = validationMatch ? validationMatch[1].trim() : data.error;
    }
    // 3. Fall back to message if it's not a generic wrapper
    else if (data.message && !['Registration failed', 'Login failed', 'Server error', 'Internal server error'].includes(data.message)) {
      msg = data.message;
    }
    // 4. Use message anyway as last resort
    else if (data.message) {
      msg = data.message;
    }
    // 5. Absolute fallback
    else {
      msg = `Request failed (${res.status})`;
    }

    throw new Error(msg);
  }
  return data;
}

// ============================================
// MAIN APP
// ============================================
export default function App() {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Legacy auth state (unused — NarbodicaAuth handles login)
  const [authLoading, setAuthLoading] = useState(false); // eslint-disable-line
  const [authError, setAuthError] = useState(''); // eslint-disable-line
  const [authMode] = useState('login'); // eslint-disable-line
  const [authForm] = useState({}); // eslint-disable-line
  const [user, setUser] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  // App state
  const [currentView, setCurrentView] = useState('discover');
  const [activeGroup, setActiveGroup] = useState(null); // for GroupChat view
  const [userGeo, setUserGeo] = useState(null); // browser geolocation fallback
  const [events, setEvents] = useState([]);
  const [recommendedEvents, setRecommendedEvents] = useState([]);
  const [myRsvps, setMyRsvps] = useState([]);
  const [connectProfile, setConnectProfile] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterScope, setFilterScope] = useState('');
  const [filterFreeOnly, setFilterFreeOnly] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState([]); // [{_id: 'Sports', count: 23}, ...]
  const [categoryTags, setCategoryTags] = useState([]); // [{tag: 'running', count: 7}, ...]
  const [activeTag, setActiveTag] = useState(null);
  const [message, setMessage] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarSource, setCalendarSource] = useState('discover'); // 'discover' or 'myevents'
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [identityVerified, setIdentityVerified] = useState(null);

  // Explore map state
  const [showExploreMap, setShowExploreMap] = useState(false);
  const [exploreInput, setExploreInput] = useState('');
  const [exploreSuggestions, setExploreSuggestions] = useState([]);
  const [showExploreSuggestions, setShowExploreSuggestions] = useState(false);
  const [exploreLocation, setExploreLocation] = useState(null); // { lat, lng, name }
  const [eventDensityData, setEventDensityData] = useState([]);
  const [densityLoading, setDensityLoading] = useState(false);
  const exploreMapRef = useRef(null);
  const exploreMapInstance = useRef(null);

  // Check auth on mount — includes cross-app SSO handoff
  useEffect(() => {
    const _hp = new URLSearchParams(window.location.search);
    const _ht = _hp.get('narb_handoff');
    if (_ht) {
      const _cu = new URL(window.location.href);
      _cu.searchParams.delete('narb_handoff');
      window.history.replaceState({}, '', _cu.toString());
      fetch(API_BASE + '/api/auth/cross-app/exchange', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: _ht }),
      }).then(r => r.json()).then(d => {
        if (d.success && d.data?.tokens) {
          localStorage.setItem('connect_token', d.data.tokens.accessToken);
          if (d.data.tokens.refreshToken) localStorage.setItem('connect_refresh', d.data.tokens.refreshToken);
          if (d.data.user) localStorage.setItem('user', JSON.stringify(d.data.user));
          setUser(d.data.user);
          setIsLoggedIn(true);
        }
      }).catch(() => {});
      return;
    }
    const token = localStorage.getItem('connect_token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsLoggedIn(true);
      } catch { localStorage.clear(); }
    }
  }, []);

  // ── Accessibility — Layer 1 (detect at app load) ──────────────────────────
  // Three-signal detection: URL override, screen reader, saved preference.
  // Toggle (rendered below) is the reliable fallback regardless.
  const accessibilityInitialise = useAccessibilityStore(s => s.initialise);
  const accessibilityHasInitialised = useAccessibilityStore(s => s.hasInitialised);
  useEffect(() => {
    if (accessibilityHasInitialised) return;
    const token = localStorage.getItem('connect_token');
    detectAccessibilityNeeds({ token, apiBase: API_URL }).then(detectorResult => {
      accessibilityInitialise({ token, apiBase: API_URL, detectorResult });
    });
  }, [isLoggedIn, accessibilityHasInitialised, accessibilityInitialise]);

  // Request browser geolocation as fallback for event proximity filtering
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {
        // Fallback: Townsville coordinates if geolocation denied
        setUserGeo({ lat: -19.2590, lng: 146.8169 });
      },
      { timeout: 5000, maximumAge: 600000 }    );
  }, []);

  // Load data when logged in
  useEffect(() => {
    if (isLoggedIn) {
      loadDiscoverEvents();
      loadRecommendedEvents();
      loadMyRsvps();
      loadConnectProfile();
      fetchCategoryCounts();
      // Load accessibility + theme preferences
      const ct = localStorage.getItem('connect_token');
      if (ct) loadAndApplyPreferences(ct).catch(() => {});
    }
  }, [isLoggedIn]);

  // Re-load events when geolocation becomes available (so proximity filter kicks in)
  useEffect(() => {
    if (isLoggedIn && userGeo) loadDiscoverEvents();
  }, [userGeo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Identity verification check
  useEffect(() => {
    const ct = localStorage.getItem('connect_token');
    if (!isLoggedIn || !ct) return;
    fetch(API_BASE + '/api/identity/status', {
      headers: { Authorization: 'Bearer ' + ct }
    }).then(r => r.json()).then(d => {
      setIdentityVerified(d?.data?.verified || false);
    }).catch(() => setIdentityVerified(true));
  }, [isLoggedIn]);

  // Show temporary message
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // ============================================
  // AUTH HANDLERS
  // ============================================

  const handleLogout = () => {
    localStorage.removeItem('connect_token');
    localStorage.removeItem('connect_refresh');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsLoggedIn(false);
    setEvents([]);
    setRecommendedEvents([]);
    setMyRsvps([]);
    setConnectProfile(null);
    setSelectedEvent(null);
    setCurrentView('discover');
  };

  // ============================================
  // DATA LOADERS
  // ============================================
  const loadDiscoverEvents = async (filters = {}) => {
    setLoading(true);
    try {
      let params = new URLSearchParams();
      const cat = filters.category !== undefined ? filters.category : activeCategory;
      const search = filters.search !== undefined ? filters.search : searchQuery;
      const scope = filters.scope !== undefined ? filters.scope : filterScope;
      const free = filters.isFree !== undefined ? filters.isFree : filterFreeOnly;
      const tags = filters.tags !== undefined ? filters.tags : activeTag;
      if (cat) params.append('category', cat);
      if (search) params.append('search', search);
      if (scope) params.append('scope', scope);
      if (free) params.append('isFree', 'true');
      if (tags) params.append('tags', tags);
      // Location-based filtering: explore map > profile location > no filter
      const loc = filters.lat ? filters : exploreLocation;
      if (loc?.lat && loc?.lng) {
        params.append('lat', loc.lat);
        params.append('lng', loc.lng);
        params.append('radius', '100');
      } else if (connectProfile?.narbodica?.lat && connectProfile?.narbodica?.lng) {
        params.append('lat', connectProfile.narbodica.lat);
        params.append('lng', connectProfile.narbodica.lng);
        params.append('radius', '100');
      } else if (connectProfile?.location?.coordinates?.length === 2) {
        params.append('lng', connectProfile.location.coordinates[0]);
        params.append('lat', connectProfile.location.coordinates[1]);
        params.append('radius', '100');
      } else if (userGeo?.lat && userGeo?.lng) {
        params.append('lat', userGeo.lat);
        params.append('lng', userGeo.lng);
        params.append('radius', '100');
      }
      params.append('limit', '30');

      const data = await api(`/api/connect/events/discover?${params}`);
      setEvents(data.data?.events || []);
    } catch (err) {
      console.error('Load events error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRecommendedEvents = async () => {
    try {
      const data = await api('/api/connect/events/feed/recommended?limit=10');
      setRecommendedEvents(data.data?.events || []);
    } catch (err) {
      console.error('Load recommended error:', err);
    }
  };

  const loadMyRsvps = async () => {
    try {
      const data = await api('/api/connect/rsvp/me?timeframe=upcoming');
      setMyRsvps(data.data?.rsvps || []);
    } catch (err) {
      console.error('Load RSVPs error:', err);
    }
  };

  const loadConnectProfile = async () => {
    try {
      const data = await api('/api/connect/profile');
      const profile = data.data?.profile || null;
      setConnectProfile(profile);

      // NarbodicaProfile is the source of truth for onboarding
      try {
        const nbRes = await fetch(
          `${API_BASE}/api/narbodica-profile/for-app/connect`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('connect_token')}` } }
        ).then(r => r.json());
        if (nbRes.success) {
          if (nbRes.data?.onboardingDone) {
            // Onboarding complete — go straight to app, do not check ConnectProfile
            return;
          } else {
            // NarbodicaProfile says not done — show onboarding
            setNeedsOnboarding(true);
            return;
          }
        }
      } catch (e) {
        console.error('NarbodicaProfile check failed:', e);
      }

      // NarbodicaProfile check failed — fall back to ConnectProfile
      if (profile && profile.onboardingComplete) return;
      if (profile && (profile.interests?.length > 0 || profile.bio)) return;
      setNeedsOnboarding(true);

    } catch (err) {
      console.error('Load profile error:', err);
      // Even on error, check NarbodicaProfile before showing onboarding
      try {
        const nbRes = await fetch(
          `${API_BASE}/api/narbodica-profile/for-app/connect`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('connect_token')}` } }
        ).then(r => r.json());
        if (nbRes.success && nbRes.data?.onboardingDone) return;
        if (nbRes.success && !nbRes.data?.onboardingDone) {
          setNeedsOnboarding(true);
          return;
        }
      } catch (e) {}
      setNeedsOnboarding(true);
    }
  };

  // ============================================
  // EVENT ACTIONS
  // ============================================
  const handleRsvp = async (eventId, status) => {
    try {
      await api(`/api/connect/rsvp/${eventId}`, {
        method: 'POST',
        body: JSON.stringify({ status, guestCount: 1 })
      });
      showMessage(status === 'going' ? "You're going! 🎉" : 'Marked as interested');
      loadMyRsvps();
      // Refresh selected event if viewing it
      if (selectedEvent?._id === eventId) {
        const data = await api(`/api/connect/events/${eventId}`);
        setSelectedEvent(data.data?.event || selectedEvent);
      }
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const handleCancelRsvp = async (eventId) => {
    try {
      await api(`/api/connect/rsvp/${eventId}`, { method: 'DELETE' });
      showMessage('RSVP cancelled');
      loadMyRsvps();
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const handleSearch = () => {
    loadDiscoverEvents({ search: searchQuery });
  };

  // Fetch category counts (sorted by popularity)
  const fetchCategoryCounts = async () => {
    try {
      const data = await api('/api/connect/events/categories');
      if (data.success) setCategoryCounts(data.data.categories || []);
    } catch (err) {
      console.error('Fetch category counts error:', err);
    }
  };

  // Fetch tags for a specific category (sub-filters)
  const fetchCategoryTags = async (category) => {
    try {
      const data = await api(`/api/connect/events/tags?category=${encodeURIComponent(category)}`);
      if (data.success) setCategoryTags(data.data.tags || []);
    } catch (err) {
      console.error('Fetch category tags error:', err);
      setCategoryTags([]);
    }
  };

  const handleCategoryFilter = (cat) => {
    const newCat = activeCategory === cat ? null : cat;
    setActiveCategory(newCat);
    setActiveTag(null);
    setCategoryTags([]);
    loadDiscoverEvents({ category: newCat || '', tags: '' });
    if (showExploreMap) fetchEventDensity({ category: newCat || '' });
    if (newCat) fetchCategoryTags(newCat);
  };

  const handleTagFilter = (tag) => {
    const newTag = activeTag === tag ? null : tag;
    setActiveTag(newTag);
    loadDiscoverEvents({ tags: newTag || '' });
    if (showExploreMap) fetchEventDensity({ tags: newTag || '' });
  };

  // After syncing from Spark, map tags to taxonomy and save
  const syncAndMapSparkInterests = async () => {
    await api('/api/connect/profile/sync-spark', { method: 'POST' });
    // Reload profile to get the synced interestTags
    const refreshed = await api('/api/connect/profile');
    const profile = refreshed.data?.profile;
    if (profile?.interestTags?.length > 0) {
      const { interests, interestGroups } = mapSparkTagsToTaxonomy(profile.interestTags);
      if (interests.length > 0) {
        await api('/api/connect/profile', {
          method: 'PUT',
          body: JSON.stringify({ interests, interestGroups })
        });
      }
    }
  };

  const handleSyncSpark = async () => {
    try {
      await syncAndMapSparkInterests();
      showMessage('Synced interests from Spark! ✨');
      loadConnectProfile();
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  // ============================================
  // EXPLORE MAP — browse events across Australia
  // ============================================

  // Load Leaflet CSS/JS from CDN
  const loadLeaflet = useCallback(() => {
    return new Promise((resolve) => {
      if (window.L) { resolve(window.L); return; }
      if (!document.getElementById('leaflet-css')) {
        const css = document.createElement('link');
        css.id = 'leaflet-css'; css.rel = 'stylesheet';
        css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(css);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => resolve(window.L);
        document.head.appendChild(script);
      } else {
        const check = setInterval(() => { if (window.L) { clearInterval(check); resolve(window.L); } }, 100);
      }
    });
  }, []);

  // Fetch event density data (events grouped by region)
  const fetchEventDensity = async (filters = {}) => {
    setDensityLoading(true);
    try {
      const token = localStorage.getItem('connect_token');
      const params = new URLSearchParams();
      const cat = filters.category !== undefined ? filters.category : activeCategory;
      const scope = filters.scope !== undefined ? filters.scope : filterScope;
      const free = filters.isFree !== undefined ? filters.isFree : filterFreeOnly;
      if (cat) params.append('category', cat);
      if (scope) params.append('scope', scope);
      if (free) params.append('isFree', 'true');
      const qs = params.toString();
      const response = await fetch(`${API_URL}/api/connect/events/density${qs ? '?' + qs : ''}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setEventDensityData(data.data);
      }
    } catch (err) {
      console.error('Error fetching event density:', err);
    } finally {
      setDensityLoading(false);
    }
  };

  // Autocomplete for city/suburb search in explore map
  const handleExploreInputChange = (value) => {
    setExploreInput(value);
    const trimmed = value.trim().toLowerCase();
    if (trimmed.length < 2) {
      setExploreSuggestions([]); setShowExploreSuggestions(false); return;
    }
    const matches = AU_PLACES
      .map(([name, state, lat, lng], idx) => ({
        name, state, lat, lng,
        display: `${name}, ${state}`,
        score: name.toLowerCase().startsWith(trimmed) ? 0 :
               name.toLowerCase().includes(trimmed) ? 1 :
               `${name} ${state}`.toLowerCase().includes(trimmed) ? 2 : 3,
        idx
      }))
      .filter(m => m.score < 3)
      .sort((a, b) => a.score - b.score || a.idx - b.idx)
      .slice(0, 5);
    setExploreSuggestions(matches);
    setShowExploreSuggestions(trimmed.length >= 2);
  };

  // Select a suggestion from autocomplete
  const handleExploreSelect = async (suggestion) => {
    setExploreInput(suggestion.name);
    setShowExploreSuggestions(false);
    setExploreSuggestions([]);
    setExploreLocation({ lat: suggestion.lat, lng: suggestion.lng, name: suggestion.name });
    // Reload events for that location
    loadDiscoverEvents({ lat: suggestion.lat, lng: suggestion.lng });
    setExploreInput('');
    console.log(`🔍 Exploring events near ${suggestion.name}`);
  };

  // Manual "Go" button — Nominatim fallback
  const handleExploreLookup = async () => {
    const query = exploreInput.trim();
    if (!query) return;
    setShowExploreSuggestions(false);
    setExploreSuggestions([]);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)},+Australia&limit=1`,
        { headers: { 'User-Agent': 'ConnectApp/1.0' } }
      );
      const results = await response.json();
      if (results.length === 0) { showMessage('Location not found. Try a different city.', 'error'); return; }
      const { lat, lon, display_name } = results[0];
      const cityName = display_name.split(',')[0].trim();
      setExploreLocation({ lat: parseFloat(lat), lng: parseFloat(lon), name: cityName });
      loadDiscoverEvents({ lat: parseFloat(lat), lng: parseFloat(lon) });
      setExploreInput('');
    } catch (err) {
      console.error('Explore lookup error:', err);
      showMessage('Failed to look up location.', 'error');
    }
  };

  // When user taps a region dot on the map
  const handleMapRegionTap = async (region) => {
    setExploreLocation({ lat: region.lat, lng: region.lng, name: region.city });
    loadDiscoverEvents({ lat: region.lat, lng: region.lng });
    setShowExploreMap(false);
  };

  // Reset explore back to local events
  const handleResetExplore = () => {
    setExploreLocation(null);
    loadDiscoverEvents();
  };

  // Density markers layer ref (so we can clear/redraw without recreating the map)
  const densityLayerRef = useRef(null);

  // Draw density markers on the existing map
  const drawDensityMarkers = (L, map, data) => {
    // Remove old markers
    if (densityLayerRef.current) {
      densityLayerRef.current.clearLayers();
    }
    const layerGroup = L.layerGroup().addTo(map);
    densityLayerRef.current = layerGroup;

    const validDensity = data.filter(d => d.city && d.city !== 'Unknown' && d.lat && d.lng);
    if (validDensity.length === 0) return;

    validDensity.forEach(region => {
      // Absolute thresholds: 1-2 = small teal, 3-5 = medium orange, 6+ = large red
      const count = region.count;
      let radius, hue, sat, light;
      if (count <= 2) {
        radius = 14;
        hue = 174; sat = 70; light = 45; // teal
      } else if (count <= 5) {
        radius = 22;
        hue = 30; sat = 85; light = 50; // orange
      } else if (count <= 10) {
        radius = 30;
        hue = 0; sat = 80; light = 50; // red
      } else {
        radius = 38;
        hue = 0; sat = 90; light = 45; // deep red
      }
      const colour = `hsl(${hue}, ${sat}%, ${light}%)`;

      const marker = L.circleMarker([region.lat, region.lng], {
        radius, fillColor: colour, fillOpacity: 0.7,
        color: colour, weight: 2, opacity: 0.9
      }).addTo(layerGroup);

      marker.bindTooltip(
        `<div style="text-align:center;font-family:inherit">
          <strong>${region.city}</strong><br/>
          <span style="font-size:13px">${region.count} ${region.count === 1 ? 'event' : 'events'}</span>
        </div>`,
        { direction: 'top', offset: [0, -radius] }
      );

      marker.on('click', () => handleMapRegionTap(region));
    });
  };

  // Initialise Leaflet map when explore panel opens or tab switches
  useEffect(() => {
    if (!showExploreMap) return;
    let cancelled = false;

    const initMap = async () => {
      const L = await loadLeaflet();
      if (cancelled || !exploreMapRef.current) return;

      if (exploreMapInstance.current) {
        exploreMapInstance.current.remove();
        exploreMapInstance.current = null;
      }

      const map = L.map(exploreMapRef.current, {
        center: [-25.5, 134], zoom: 4, minZoom: 3, maxZoom: 12,
        zoomControl: true, attributionControl: false
      });

      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19, subdomains: 'abcd'
      }).addTo(map);

      exploreMapInstance.current = map;

      // Draw initial markers
      drawDensityMarkers(L, map, eventDensityData);

      setTimeout(() => { if (map) map.invalidateSize(); }, 200);
    };

    initMap();
    return () => { cancelled = true; };
  }, [showExploreMap, loadLeaflet, currentView]);

  // Update markers when density data changes (without recreating the map)
  useEffect(() => {
    if (!showExploreMap || !exploreMapInstance.current) return;

    const updateMarkers = async () => {
      const L = await loadLeaflet();
      drawDensityMarkers(L, exploreMapInstance.current, eventDensityData);
    };

    updateMarkers();
  }, [eventDensityData]);

  // Cleanup map when closing
  useEffect(() => {
    if (!showExploreMap && exploreMapInstance.current) {
      exploreMapInstance.current.remove();
      exploreMapInstance.current = null;
    }
  }, [showExploreMap]);

  // ============================================
  // LOGIN / REGISTER SCREEN
  // ============================================
  // ============================================
  // LOGIN SCREEN — NarbodicaAuth
  // ============================================
  if (!isLoggedIn) {
    return (
      <div style={{ minHeight: '100dvh', background: '#09090b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AccessibilityToggle accentColor="#14b8a6" apiBase={API_URL} />
        <div style={{ width: '100%', maxWidth: 420, minHeight: '100dvh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <NarbodicaAuth
            app="connect"
            appLabel="Connect"
            appTagline="Find your people"
            accentColor="#14b8a6"
            apiBase={API_URL}
            onSuccess={(tokens, user) => {
              const token = tokens?.accessToken || tokens?.token;
              const refresh = tokens?.refreshToken;
              if (token) {
                localStorage.setItem('connect_token', token);
                if (refresh) localStorage.setItem('connect_refresh', refresh);
                localStorage.setItem('user', JSON.stringify(user));
                setUser(user);
                setIsLoggedIn(true);
                // Load accessibility + theme preferences
                loadAndApplyPreferences(token).catch(() => {});
              }
            }}
          />
        </div>
      </div>
    );
  }

  // Identity verification gate — verify once, access everywhere
  const connectToken = localStorage.getItem('connect_token');

  if (isLoggedIn && identityVerified === false) {
    return (
      <>
        <AccessibilityToggle accentColor="#14b8a6" token={connectToken} apiBase={API_URL} />
        <IdentityGate token={connectToken} tokenKey="connect_token" accent="#14b8a6">
          <div />
        </IdentityGate>
      </>
    );
  }

  if (isLoggedIn && needsOnboarding) {
    return (
      <>
        <AccessibilityToggle accentColor="#14b8a6" token={localStorage.getItem('connect_token')} apiBase={API_URL} />
        <NarbodicaProfileOnboarding
          app="connect"
          appLabel="Connect"
          accentColor="#14b8a6"
          token={localStorage.getItem('connect_token')}
          apiBase={API_BASE}
          onComplete={() => {
            setNeedsOnboarding(false);
            loadConnectProfile();
            loadDiscoverEvents();
            loadRecommendedEvents();
          }}
        />
      </>
    );
  }

  // ============================================
  // EVENT DETAIL VIEW
  // ============================================
  if (selectedEvent) {
    const cat = getCategoryConfig(selectedEvent.category);
    const CatIcon = cat.icon;
    const userRsvp = myRsvps.find(r => r.eventId?._id === selectedEvent._id || r.eventId === selectedEvent._id);

    return (
      <div style={shellStyle}>
        <style>{globalStyles}</style>
        {message && <Toast message={message} />}
        <div style={containerStyle}>
          {/* Back header */}
          <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #27272a' }}>
            <button onClick={() => setSelectedEvent(null)} style={iconBtnStyle}>
              <ArrowLeft size={20} />
            </button>
            <span style={{ color: '#fafafa', fontWeight: 600, flex: 1 }}>Event Details</span>
            <button style={iconBtnStyle}><ShareNetwork size={18} /></button>
          </div>

          <div style={{ overflow: 'auto', flex: 1 }}>
            {/* Cover image */}
            {selectedEvent.coverImage?.url ? (
              <div style={{ height: '200px', background: `url(${selectedEvent.coverImage.url}) center/cover`, position: 'relative' }}>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '80px', background: 'linear-gradient(transparent, #09090b)' }} />
              </div>
            ) : (
              <div style={{ height: '120px', background: cat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CatIcon size={48} color={cat.color} style={{ opacity: 0.6 }} />
              </div>
            )}

            <div style={{ padding: '20px' }}>
              {/* Category badge */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: cat.bg, padding: '6px 12px', borderRadius: '20px', marginBottom: '12px' }}>
                <CatIcon size={14} color={cat.color} />
                <span style={{ color: cat.color, fontSize: '12px', fontWeight: 600 }}>{selectedEvent.category}</span>
              </div>

              {/* Title */}
              <h2 style={{ color: '#fafafa', fontSize: '22px', fontFamily: '"DM Serif Display", serif', margin: '0 0 12px', lineHeight: 1.3 }}>
                {selectedEvent.title}
              </h2>

              {/* External event — prominent link right after title */}
              {selectedEvent.source && selectedEvent.source !== 'user' && selectedEvent.source !== 'organizer' && (selectedEvent.externalUrl || selectedEvent.pricing?.ticketUrl) && (
                <a
                  href={selectedEvent.externalUrl || selectedEvent.pricing?.ticketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`View event on ${selectedEvent.source === 'ticketmaster' ? 'Ticketmaster' : 'event site'}`}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: '100%', padding: '12px', marginBottom: 16,
                    background: '#2dd4bf', color: '#000', border: 'none', borderRadius: 12,
                    fontWeight: 700, fontSize: 14, textDecoration: 'none', fontFamily: 'inherit',
                  }}
                >
                  <Globe size={16} />
                  View on {selectedEvent.source === 'ticketmaster' ? 'Ticketmaster' : selectedEvent.source || 'event site'}
                </a>
              )}

              {/* Quick info cards */}
              <div style={{ display: 'grid', gap: '10px', marginBottom: '20px' }}>
                <InfoRow icon={Calendar} label={`${formatDate(selectedEvent.startDate)} · ${formatTime(selectedEvent.startDate)} — ${formatTime(selectedEvent.endDate)}`} />
                <InfoRow icon={MapPin} label={selectedEvent.location?.venueName || selectedEvent.location?.address?.city || 'Online'} sublabel={selectedEvent.location?.address ? `${selectedEvent.location.address.street || ''}, ${selectedEvent.location.address.city || ''}` : ''} />
                <InfoRow icon={Ticket} label={formatPrice(selectedEvent.pricing)} sublabel={selectedEvent.pricing?.isFree ? 'No ticket required' : selectedEvent.pricing?.ticketUrl ? 'External ticketing' : ''} />
                {selectedEvent.capacity?.max && (
                  <InfoRow icon={Users} label={`${selectedEvent.capacity.current || 0} / ${selectedEvent.capacity.max} spots`} sublabel={selectedEvent.spotsRemaining > 0 ? `${selectedEvent.spotsRemaining} remaining` : 'Full'} />
                )}
              </div>

              {/* Organizer */}
              <div style={{ background: '#18181b', borderRadius: '12px', padding: '14px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: cat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <User size={20} color={cat.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fafafa', fontSize: '14px', fontWeight: 600 }}>{selectedEvent.organizer?.name}</div>
                  <div style={{ color: '#71717a', fontSize: '12px' }}>
                    {selectedEvent.organizer?.isVerifiedOrganizer ? '✓ Verified Organizer' : 'Event Host'}
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedEvent.description ? (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ color: '#a1a1aa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: 600 }}>About</h3>
                  <p style={{ color: '#d4d4d8', fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{selectedEvent.description}</p>
                </div>
              ) : selectedEvent.source && selectedEvent.source !== 'user' && selectedEvent.source !== 'organizer' ? (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ color: '#a1a1aa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: 600 }}>About</h3>
                  <p style={{ color: '#71717a', fontSize: '13px', lineHeight: 1.5, fontStyle: 'italic' }}>
                    No description available. Visit the event page for full details.
                  </p>
                </div>
              ) : null}

              {/* Tags */}
              {selectedEvent.tags?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ color: '#a1a1aa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: 600 }}>Tags</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {selectedEvent.tags.map(tag => (
                      <span key={tag} style={{ background: '#18181b', color: '#a1a1aa', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* External event — bottom link + note */}
              {selectedEvent.source && selectedEvent.source !== 'user' && selectedEvent.source !== 'organizer' && (
                <div style={{ background: 'rgba(45,212,191,0.04)', border: '1px solid rgba(45,212,191,0.1)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
                  <p style={{ color: '#a1a1aa', fontSize: 12, margin: '0 0 10px', lineHeight: 1.4 }}>
                    This event is hosted externally. For availability, tickets, and full details visit the event page.
                  </p>
                  {(selectedEvent.externalUrl || selectedEvent.pricing?.ticketUrl) && (
                    <a
                      href={selectedEvent.externalUrl || selectedEvent.pricing?.ticketUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        width: '100%', padding: '10px', background: 'transparent',
                        border: '1px solid #2dd4bf', borderRadius: 10, color: '#2dd4bf',
                        fontWeight: 600, fontSize: 13, textDecoration: 'none', fontFamily: 'inherit',
                      }}
                    >
                      <Globe size={14} />
                      View on {selectedEvent.source === 'ticketmaster' ? 'Ticketmaster' : selectedEvent.source || 'event site'}
                    </a>
                  )}
                </div>
              )}

              {/* Stats — only for community events */}
              {(!selectedEvent.source || selectedEvent.source === 'user' || selectedEvent.source === 'organizer') && (
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', padding: '14px', background: '#18181b', borderRadius: '12px' }}>
                  <StatBadge icon={Eye} value={selectedEvent.stats?.views || 0} label="views" />
                  <StatBadge icon={BookmarkSimple} value={selectedEvent.stats?.interested || 0} label="interested" />
                  <StatBadge icon={Check} value={selectedEvent.stats?.going || 0} label="going" />
                </div>
              )}

              {/* RSVP Buttons — only for community events */}
              {(!selectedEvent.source || selectedEvent.source === 'user' || selectedEvent.source === 'organizer') && (
              <div style={{ display: 'flex', gap: '10px', paddingBottom: '24px' }}>
                {userRsvp?.status === 'going' ? (
                  <button onClick={() => handleCancelRsvp(selectedEvent._id)} style={{ ...primaryButtonStyle, background: '#27272a', color: '#a1a1aa', flex: 1 }}>
                    ✓ Going — Tap to Cancel
                  </button>
                ) : userRsvp?.status === 'interested' ? (
                  <>
                    <button onClick={() => handleRsvp(selectedEvent._id, 'going')} style={{ ...primaryButtonStyle, flex: 1 }}>
                      I'm Going! 🎉
                    </button>
                    <button onClick={() => handleCancelRsvp(selectedEvent._id)} style={{ ...iconBtnStyle, width: '48px', height: '48px' }}>
                      <X size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleRsvp(selectedEvent._id, 'interested')} style={{ flex: 1, padding: '14px', border: '1px solid #27272a', borderRadius: '12px', background: 'transparent', color: '#fafafa', fontWeight: 600, cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit' }}>
                      <BookmarkSimple size={16} style={{ marginRight: '6px', verticalAlign: '-3px' }} /> Interested
                    </button>
                    <button onClick={() => handleRsvp(selectedEvent._id, 'going')} style={{ ...primaryButtonStyle, flex: 1 }}>
                      I'm Going! 🎉
                    </button>
                  </>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN APP
  // ============================================
  return (
    <div style={shellStyle}>
      <AccessibilityToggle accentColor="#14b8a6" token={connectToken} apiBase={API_URL} />
      <style>{globalStyles}</style>
      {message && <Toast message={message} />}
      {showCalendar && (
        <CalendarModal
          events={calendarSource === 'discover' ? events : myRsvps.map(r => r.eventId).filter(Boolean)}
          source={calendarSource}
          onClose={() => setShowCalendar(false)}
          onSelectEvent={(event) => { setShowCalendar(false); setSelectedEvent(event); }}
          activeCategory={calendarSource === 'discover' ? activeCategory : null}
        />
      )}

      <div style={containerStyle}>
        {/* Floating create button */}
        {currentView !== 'create' && currentView !== 'settings' && currentView !== 'profile' && (
          <button
            onClick={() => setCurrentView('create')}
            style={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 100,
              width: 56, height: 56, borderRadius: 16,
              background: '#2dd4bf', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(45,212,191,0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(45,212,191,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(45,212,191,0.3)'; }}
            title="Create an event"
          >
            <Plus size={24} color="#09090b" strokeWidth={2.5} />
          </button>
        )}

        {/* ── HEADER ── */}
        <div style={{ padding: '16px 20px 0', borderBottom: '1px solid #1c1c1f' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Compass size={28} color="#2dd4bf" />
            <h1 style={{ margin: 0, fontSize: '24px', fontFamily: '"DM Serif Display", serif', color: '#fafafa' }}>Connect</h1>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#a1a1aa', fontSize: '13px' }}>{user?.name}</span>
              <button onClick={() => setCurrentView('settings')} style={iconBtnStyle}><Gear size={18} /></button>
              <button onClick={handleLogout} style={iconBtnStyle}><SignOut size={18} /></button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div style={{ display: 'flex', gap: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '3px', marginBottom: '16px' }}>
            {[
              { id: 'discover', icon: Compass, label: 'Discover' },
              { id: 'groups', icon: Users, label: 'Groups' },
              { id: 'profile', icon: User, label: 'Profile' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setCurrentView(tab.id); setActiveGroup(null); }}
                aria-label={tab.label}
                aria-current={currentView === tab.id ? 'page' : undefined}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                  padding: '8px 4px', border: 'none', borderRadius: '10px', cursor: 'pointer',
                  background: currentView === tab.id ? '#18181b' : 'transparent',
                  color: currentView === tab.id ? '#2dd4bf' : 'rgba(255,255,255,0.35)',
                  fontWeight: 600, fontSize: '10px', fontFamily: 'inherit',
                  transition: 'all 0.2s',
                  boxShadow: currentView === tab.id ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                }}
              >
                <tab.icon size={18} aria-hidden="true" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── CONTENT AREA ── */}
        <div style={{ flex: 1, overflow: 'auto' }}>

          {/* ═══════════════════════════════════ */}
          {/* CREATE EVENT */}
          {/* ═══════════════════════════════════ */}
          {currentView === 'create' && (
            <CreateEvent
              token={localStorage.getItem('connect_token')}
              onDone={(event) => {
                setCurrentView('myevents');
                loadMyRsvps();
                showMessage('Event created! People can now find it.');
              }}
              onBack={() => setCurrentView('discover')}
            />
          )}

          {/* ═══════════════════════════════════ */}
          {/* DISCOVER TAB */}
          {/* ═══════════════════════════════════ */}
          {currentView === 'discover' && (
            <div style={{ padding: '16px 20px' }}>

              {/* Try Something New */}
              <TrySomethingNew
                token={localStorage.getItem('connect_token')}
                userInterests={connectProfile?.interestGroups || []}
                onSelectEvent={(event) => setSelectedEvent(event)}
              />

              {/* Search */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <MagnifyingGlass size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#71717a' }} aria-hidden="true" />
                  <input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    placeholder="Search events..."
                    aria-label="Search events"
                    style={{ ...inputStyle, paddingLeft: '36px', marginBottom: 0 }}
                  />
                </div>
                <button onClick={() => setShowFilters(!showFilters)}
                  aria-label="Filters"
                  aria-expanded={showFilters}
                  style={{ ...iconBtnStyle, width: '44px', height: '44px', background: showFilters ? 'rgba(45,212,191,0.15)' : '#18181b', color: showFilters ? '#2dd4bf' : '#a1a1aa' }}>
                  <Funnel size={18} />
                </button>
              </div>

              {/* Filters (hidden by default) */}
              {showFilters && (
                <div className="fade-in" style={{ background: '#18181b', borderRadius: '12px', padding: '14px', marginBottom: '12px', border: '1px solid #27272a' }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                    {['', 'local', 'statewide', 'nationwide', 'online'].map(s => (
                      <button key={s} onClick={() => { setFilterScope(s); loadDiscoverEvents({ scope: s }); if (showExploreMap) fetchEventDensity({ scope: s }); }}
                        style={{ padding: '6px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                          background: filterScope === s ? 'rgba(45,212,191,0.15)' : '#27272a',
                          color: filterScope === s ? '#2dd4bf' : '#a1a1aa'
                        }}>
                        {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a1a1aa', fontSize: '13px', cursor: 'pointer', marginBottom: '10px' }}>
                    <input type="checkbox" checked={filterFreeOnly} onChange={e => { setFilterFreeOnly(e.target.checked); loadDiscoverEvents({ isFree: e.target.checked }); if (showExploreMap) fetchEventDensity({ isFree: e.target.checked }); }} />
                    Free events only
                  </label>
                  <button
                    onClick={() => { setShowExploreMap(!showExploreMap); if (!showExploreMap && eventDensityData.length === 0) fetchEventDensity(); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#2dd4bf', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit', padding: 0 }}
                  >
                    <Globe size={13} /> {showExploreMap ? 'Hide map' : 'Explore a different area'}
                  </button>
                </div>
              )}

              {/* Explore Map (shown when toggled from filters) */}
              {showExploreMap && (
                <div className="fade-in" style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '14px', padding: '14px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ color: '#fafafa', fontSize: '14px', fontWeight: 700 }}>Explore Australia</span>
                    <button onClick={() => setShowExploreMap(false)} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer' }}><X size={16} /></button>
                  </div>
                  <div ref={exploreMapRef} style={{ width: '100%', height: '180px', borderRadius: '10px', overflow: 'hidden', background: '#09090b', marginBottom: '8px' }} />
                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input value={exploreInput} onChange={e => handleExploreInputChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleExploreLookup()}
                        placeholder="Type a city name..." aria-label="Search city"
                        style={{ flex: 1, padding: '9px 12px', background: '#09090b', border: '1px solid #27272a', borderRadius: '10px', color: '#fafafa', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }} />
                      <button onClick={handleExploreLookup} style={{ padding: '9px 14px', background: '#2dd4bf', color: '#09090b', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Go</button>
                    </div>
                    {showExploreSuggestions && exploreSuggestions.length > 0 && (
                      <div style={{ position: 'absolute', left: 0, right: 52, bottom: '100%', marginBottom: '4px', background: '#27272a', border: '1px solid #3f3f46', borderRadius: '10px', overflow: 'hidden', zIndex: 50 }}>
                        {exploreSuggestions.map((s, i) => (
                          <button key={i} onClick={() => handleExploreSelect(s)}
                            style={{ display: 'block', width: '100%', padding: '9px 12px', background: 'transparent', border: 'none', borderBottom: i < exploreSuggestions.length - 1 ? '1px solid #3f3f46' : 'none', color: '#fafafa', fontSize: '12px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#3f3f46'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <span style={{ fontWeight: 600 }}>{s.name}</span><span style={{ color: '#71717a' }}>, {s.state}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Explore location active banner */}
              {exploreLocation && (
                <div className="fade-in" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'rgba(45,212,191,0.06)', border: '1px solid rgba(45,212,191,0.15)', borderRadius: '10px', marginBottom: '12px', fontSize: '12px' }}>
                  <MapPin size={13} color="#2dd4bf" />
                  <span style={{ flex: 1, color: '#d4d4d8', fontWeight: 600 }}>Near {exploreLocation.name}</span>
                  <button onClick={handleResetExplore} style={{ background: 'none', border: 'none', color: '#2dd4bf', cursor: 'pointer', fontSize: '11px', fontWeight: 600, fontFamily: 'inherit' }}>Reset</button>
                </div>
              )}

              {/* Category Pills — clean, no counts */}
              <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '6px', WebkitOverflowScrolling: 'touch' }}>
                {(categoryCounts.length > 0
                  ? categoryCounts.map(c => c._id).filter(c => CATEGORY_CONFIG[c] && c !== 'Other')
                  : Object.keys(CATEGORY_CONFIG).filter(c => c !== 'Other')
                ).map(cat => {
                  const cfg = CATEGORY_CONFIG[cat];
                  if (!cfg) return null;
                  const Icon = cfg.icon;
                  const isActive = activeCategory === cat;
                  return (
                    <button key={cat} onClick={() => handleCategoryFilter(cat)}
                      aria-label={cat}
                      aria-pressed={isActive}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px',
                        borderRadius: '20px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                        fontSize: '11px', fontWeight: 600, fontFamily: 'inherit', flexShrink: 0,
                        background: isActive ? cfg.bg : '#18181b',
                        color: isActive ? cfg.color : '#71717a',
                        transition: 'all 0.2s'
                      }}>
                      <Icon size={12} aria-hidden="true" />
                      {cat.split(' & ')[0]}
                    </button>
                  );
                })}
              </div>

              {/* Sub-tag filters (when a category is selected) */}
              {activeCategory && categoryTags.length > 0 && (
                <div className="fade-in" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', paddingBottom: '12px', marginBottom: '4px' }}>
                  {categoryTags.map(({ tag, count }) => {
                    const isActive = activeTag === tag;
                    const cfg = CATEGORY_CONFIG[activeCategory] || CATEGORY_CONFIG['Other'];
                    return (
                      <button key={tag} onClick={() => handleTagFilter(tag)}
                        style={{
                          padding: '5px 10px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                          fontSize: '11px', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
                          background: isActive ? cfg.bg : 'rgba(39,39,42,0.8)',
                          color: isActive ? cfg.color : '#a1a1aa',
                          transition: 'all 0.2s'
                        }}>
                        {tag}
                        <span style={{ fontSize: '9px', opacity: 0.5, marginLeft: '4px' }}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Event Cards */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#71717a' }}>
                  <ArrowsClockwise size={24} className="spin" style={{ marginBottom: '12px' }} />
                  <p>Finding events...</p>
                </div>
              ) : events.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#71717a' }}>
                  <Calendar size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <p style={{ fontSize: '15px', fontWeight: 600, color: '#a1a1aa' }}>No events found</p>
                  <p style={{ fontSize: '13px' }}>Try adjusting your filters or check back later</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {events.map(event => (
                    <EventCard key={event._id} event={event} onClick={() => setSelectedEvent(event)} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════ */}
          {/* RECOMMENDED (FOR YOU) TAB */}
          {/* ═══════════════════════════════════ */}
          {currentView === 'recommended' && (
            <div style={{ padding: '16px 20px' }}>

              {/* Explore Location Banner */}
              {exploreLocation && (
                <div className="fade-in" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(45,212,191,0.08)', border: '1px solid rgba(45,212,191,0.2)', borderRadius: '12px', marginBottom: '12px' }}>
                  <Globe size={16} color="#2dd4bf" />
                  <span style={{ flex: 1, color: '#fafafa', fontSize: '13px', fontWeight: 600 }}>Showing events near {exploreLocation.name}</span>
                  <button onClick={handleResetExplore} style={{ background: 'none', border: 'none', color: '#2dd4bf', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit' }}>✕ Reset</button>
                </div>
              )}

              {/* Explore a different area toggle */}
              <button
                onClick={() => { setShowExploreMap(!showExploreMap); if (!showExploreMap && eventDensityData.length === 0) fetchEventDensity(); }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#2dd4bf', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', padding: '0 0 12px 0', opacity: 0.8 }}
              >
                <Globe size={14} /> Explore a different area
              </button>

              {/* Explore Map Panel (same as Discover) */}
              {showExploreMap && (
                <div className="fade-in" style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '16px', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Globe size={16} color="#2dd4bf" />
                      <span style={{ color: '#fafafa', fontSize: '15px', fontWeight: 700 }}>Explore Australia</span>
                    </div>
                    <button onClick={() => setShowExploreMap(false)} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer' }}><X size={18} /></button>
                  </div>
                  <div ref={exploreMapRef} style={{ width: '100%', height: '220px', borderRadius: '12px', overflow: 'hidden', background: '#09090b', marginBottom: '8px' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', fontSize: '11px', color: '#71717a' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'hsl(174, 70%, 45%)' }} /> Fewer</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'hsl(30, 85%, 50%)' }} /> More</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'hsl(0, 90%, 50%)' }} /> Most</span>
                    <span style={{ marginLeft: 'auto', fontStyle: 'italic' }}>Tap a dot to explore that area</span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        value={exploreInput}
                        onChange={e => handleExploreInputChange(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleExploreLookup()}
                        placeholder="Or type a city name..."
                        style={{ flex: 1, padding: '10px 14px', background: '#09090b', border: '1px solid #27272a', borderRadius: '10px', color: '#fafafa', fontSize: '14px', fontFamily: 'inherit', outline: 'none' }}
                      />
                      <button onClick={handleExploreLookup} style={{ padding: '10px 16px', background: '#2dd4bf', color: '#09090b', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Go</button>
                    </div>
                    {showExploreSuggestions && exploreSuggestions.length > 0 && (
                      <div style={{ position: 'absolute', left: 0, right: 52, bottom: '100%', marginBottom: '4px', background: '#27272a', border: '1px solid #3f3f46', borderRadius: '10px', overflow: 'hidden', zIndex: 50 }}>
                        {exploreSuggestions.map((s, i) => (
                          <button key={i} onClick={() => handleExploreSelect(s)}
                            style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', borderBottom: i < exploreSuggestions.length - 1 ? '1px solid #3f3f46' : 'none', color: '#fafafa', fontSize: '13px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#3f3f46'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 600 }}>{s.name}</span>
                            <span style={{ color: '#71717a' }}>, {s.state}</span>
                          </button>
                        ))}
                        {exploreInput.trim().length >= 2 && (
                          <div style={{ padding: '8px 14px', fontSize: '11px', color: '#52525b', borderTop: '1px solid #3f3f46' }}>Not listed? Press Go to search</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Sparkle size={18} color="#2dd4bf" />
                <h2 style={{ margin: 0, color: '#fafafa', fontSize: '16px', fontWeight: 700 }}>Recommended For You</h2>
              </div>
              <p style={{ color: '#71717a', fontSize: '13px', marginBottom: '20px' }}>
                Based on your interests{connectProfile?.interestsImportedFromSpark ? ' (synced from Spark)' : ''}
              </p>

              {recommendedEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#71717a' }}>
                  <Sparkle size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <p style={{ fontSize: '15px', fontWeight: 600, color: '#a1a1aa' }}>No recommendations yet</p>
                  <p style={{ fontSize: '13px', marginBottom: '16px' }}>Set up your interests to get personalised event suggestions</p>
                  <button onClick={() => setCurrentView('profile')} style={{ ...primaryButtonStyle, display: 'inline-flex' }}>
                    Set Up Interests
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {recommendedEvents.map(event => (
                    <EventCard key={event._id} event={event} onClick={() => setSelectedEvent(event)} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════ */}
          {/* MY EVENTS TAB */}
          {/* ═══════════════════════════════════ */}
          {currentView === 'myevents' && (
            <div style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Ticket size={18} color="#2dd4bf" />
                <h2 style={{ margin: 0, color: '#fafafa', fontSize: '16px', fontWeight: 700, flex: 1 }}>My Events</h2>
                <button onClick={() => { setCalendarSource('myevents'); setShowCalendar(true); }} style={{ ...iconBtnStyle, width: '36px', height: '36px' }}>
                  <Calendar size={16} />
                </button>
              </div>

              {myRsvps.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#71717a' }}>
                  <Ticket size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <p style={{ fontSize: '15px', fontWeight: 600, color: '#a1a1aa' }}>No upcoming events</p>
                  <p style={{ fontSize: '13px', marginBottom: '16px' }}>Find events you're interested in and RSVP</p>
                  <button onClick={() => setCurrentView('discover')} style={{ ...primaryButtonStyle, display: 'inline-flex' }}>
                    Discover Events
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {myRsvps.map(rsvp => {
                    const event = rsvp.eventId;
                    if (!event) return null;
                    return (
                      <div key={rsvp._id} onClick={() => setSelectedEvent(event)} style={{ cursor: 'pointer' }}>
                        <EventCard event={event} rsvpStatus={rsvp.status} onClick={() => setSelectedEvent(event)} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════ */}
          {/* SETTINGS */}
          {/* ═══════════════════════════════════ */}
                    {/* ═══════════════════════════════════════════════════════════ */}
          {/* ═══════════════════════════════════ */}
          {/* GROUPS TAB */}
          {/* ═══════════════════════════════════ */}
          {currentView === 'groups' && !activeGroup && (
            <GroupsView
              token={localStorage.getItem('connect_token')}
              user={user}
              showMessage={showMessage}
              onOpenGroup={(group) => setActiveGroup(group)}
            />
          )}

          {/* ═══════════════════════════════════ */}
          {/* GROUP CHAT (when a group is selected) */}
          {/* ═══════════════════════════════════ */}
          {currentView === 'groups' && activeGroup && (
            <GroupChat
              group={activeGroup}
              token={localStorage.getItem('connect_token')}
              user={user}
              showMessage={showMessage}
              onBack={() => setActiveGroup(null)}
            />
          )}

          {/* ═══════════════════════════════════ */}
          {/* ACCESSIBILITY (in settings) */}
          {/* ═══════════════════════════════════ */}
          {currentView === 'accessibility' && (
            <div style={{ padding: '0 20px 20px' }}>
              <button onClick={() => setCurrentView('settings')} style={{
                display: 'flex', alignItems: 'center', gap: 6, background: 'none',
                border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: '8px 0', fontSize: 13, fontFamily: 'inherit',
              }}><ArrowLeft size={16} /> Back to Settings</button>
              <AccessibilitySettings token={localStorage.getItem('connect_token')} />
            </div>
          )}

          {currentView === 'settings' && (
            <SettingsView
              user={user}
              onBack={() => setCurrentView('discover')}
              onLogout={handleLogout}
              api={api}
              showMessage={showMessage}
              onSetupProfile={() => setNeedsOnboarding(true)}
              onAccessibility={() => setCurrentView('accessibility')}
            />
          )}

          {/* ═══════════════════════════════════ */}
          {/* PROFILE TAB */}
          {/* ═══════════════════════════════════ */}
          {currentView === 'profile' && (
            <ProfileView
              user={user}
              connectProfile={connectProfile}
              onSetupProfile={() => setNeedsOnboarding(true)}
              onSyncSpark={handleSyncSpark}
              onUpdateProfile={async (updates) => {
                try {
                  await api('/api/connect/profile', { method: 'PUT', body: JSON.stringify(updates) });
                  showMessage('Profile updated! ✓');
                  loadConnectProfile();
                } catch (err) {
                  showMessage(err.message, 'error');
                }
              }}
            />
          )}

        </div>
      </div>
    </div>
  );
}

// ============================================
// CONNECT ONBOARDING WIZARD
// ============================================
function OnboardingWizard({ user, api, showMessage, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [checkingSpark, setCheckingSpark] = useState(true);
  const [hasSparkProfile, setHasSparkProfile] = useState(false);
  const [syncedFromSpark, setSyncedFromSpark] = useState(false);
  const [interestSearch, setInterestSearch] = useState('');
  const [activeGroupTab, setActiveGroupTab] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    photo: null,
    bio: '',
    interestGroups: [],
    interests: [],
    location: { city: '', state: '' },
    eventPreferences: {
      maxDistance: 50,
      preferredCategories: [],
      preferFree: false
    }
  });

  useEffect(() => {
    checkSparkProfile();
  }, []);

  const checkSparkProfile = async () => {
    try {
      const data = await api('/api/connect/profile/check-spark');
      setHasSparkProfile(data.data?.hasSpark || false);
    } catch {
      setHasSparkProfile(false);
    }
    setCheckingSpark(false);
  };

  const syncFromSpark = async () => {
    setSaving(true);
    try {
      await api('/api/connect/profile/sync-spark', { method: 'POST' });
      // Map Spark tags to Connect taxonomy interests
      const refreshed = await api('/api/connect/profile');
      const profile = refreshed.data?.profile;
      if (profile?.interestTags?.length > 0) {
        const mapped = mapSparkTagsToTaxonomy(profile.interestTags);
        if (mapped.interests.length > 0) {
          await api('/api/connect/profile', {
            method: 'PUT',
            body: JSON.stringify({ interests: mapped.interests, interestGroups: mapped.interestGroups, onboardingComplete: true })
          });
        } else {
          await api('/api/connect/profile', { method: 'PUT', body: JSON.stringify({ onboardingComplete: true }) });
        }
      } else {
        await api('/api/connect/profile', { method: 'PUT', body: JSON.stringify({ onboardingComplete: true }) });
      }
      showMessage('Profile synced from Spark ✨');
      onComplete();
    } catch (err) {
      showMessage(err.message || 'Sync failed', 'error');
    }
    setSaving(false);
  };

  const steps = [
    { title: 'Welcome', emoji: '👋' },
    { title: 'Photo', emoji: '📸' },
    { title: 'About You', emoji: '✍️' },
    { title: 'Interests', emoji: '🎯' },
    { title: 'Location', emoji: '📍' },
    { title: 'All Set', emoji: '🎉' }
  ];

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Photo must be less than 5MB'); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const token = localStorage.getItem('connect_token');
      const res = await fetch(`${API_URL}/api/connect/profile/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (data.success || data.data?.photo) {
        setFormData(prev => ({ ...prev, photo: data.data?.photo || data.photo }));
      } else { setError(data.message || 'Upload failed'); }
    } catch { setError('Upload failed. Try again.'); }
    setUploading(false);
  };

  const toggleGroup = (groupId) => {
    setFormData(prev => {
      if (prev.interestGroups.includes(groupId)) {
        const gi = INTERESTS_TAXONOMY[groupId].interests;
        return { ...prev, interestGroups: prev.interestGroups.filter(g => g !== groupId), interests: prev.interests.filter(i => !gi.includes(i)) };
      }
      return { ...prev, interestGroups: [...prev.interestGroups, groupId] };
    });
  };

  const toggleInterest = (interest, groupId) => {
    setFormData(prev => {
      if (prev.interests.includes(interest)) {
        return { ...prev, interests: prev.interests.filter(i => i !== interest) };
      }
      const newGroups = prev.interestGroups.includes(groupId) ? prev.interestGroups : [...prev.interestGroups, groupId];
      return { ...prev, interests: [...prev.interests, interest], interestGroups: newGroups };
    });
  };

  const toggleCategory = (cat) => {
    setFormData(prev => {
      const cats = prev.eventPreferences.preferredCategories;
      return { ...prev, eventPreferences: { ...prev.eventPreferences, preferredCategories: cats.includes(cat) ? cats.filter(c => c !== cat) : [...cats, cat] } };
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return true;
      case 1: return true; // photo optional
      case 2: return formData.bio.length >= 10;
      case 3: return formData.interests.length >= 3;
      case 4: return true;
      case 5: return true;
      default: return false;
    }
  };

  const saveProfile = async () => {
    setSaving(true); setError('');
    try {
      await api('/api/connect/profile', {
        method: 'PUT',
        body: JSON.stringify({
          bio: formData.bio,
          interestGroups: formData.interestGroups,
          interests: formData.interests,
          location: formData.location.city ? formData.location : undefined,
          eventPreferences: formData.eventPreferences,
          onboardingComplete: true
        })
      });
      return true;
    } catch (err) { setError(err.message || 'Failed to save'); return false; }
    finally { setSaving(false); }
  };

  const nextStep = async () => {
    setError('');
    if (currentStep === 4) {
      const ok = await saveProfile();
      if (ok) setCurrentStep(currentStep + 1);
    } else if (currentStep === steps.length - 1) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };

  const searchResults = interestSearch.length >= 2
    ? Object.entries(INTERESTS_TAXONOMY).flatMap(([gId, g]) =>
        g.interests.filter(i => i.toLowerCase().includes(interestSearch.toLowerCase())).map(i => ({ interest: i, groupId: gId, group: g }))
      ).slice(0, 20)
    : [];

  const activeGroup = activeGroupTab ? INTERESTS_TAXONOMY[activeGroupTab] : null;

  if (checkingSpark) {
    return (
      <div style={{ minHeight: '100dvh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Inter", sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <Compass size={40} color="#2dd4bf" style={{ animation: 'spin 2s linear infinite', marginBottom: '16px' }} />
          <p style={{ color: '#71717a', fontSize: '14px' }}>Setting things up...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#000', fontFamily: '"Inter", -apple-system, sans-serif', display: 'flex', justifyContent: 'center', padding: '0' }}>
      <style>{globalStyles}</style>
      <div style={containerStyle}>
        {/* Progress header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #1c1c1f' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Compass size={24} color="#2dd4bf" />
            <span style={{ fontSize: '18px', fontFamily: '"DM Serif Display", serif', color: '#fafafa' }}>Connect</span>
            <span style={{ marginLeft: 'auto', color: '#52525b', fontSize: '12px' }}>Step {currentStep + 1} of {steps.length}</span>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {steps.map((_, i) => (
              <div key={i} style={{ flex: 1, height: '3px', borderRadius: '2px', background: i <= currentStep ? '#2dd4bf' : '#27272a', transition: 'background 0.3s' }} />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="fade-in" style={{ flex: 1, overflow: 'auto', padding: '24px 20px' }}>

          {/* Step 0: Welcome */}
          {currentStep === 0 && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>👋</span>
                <h2 style={{ color: '#fafafa', fontSize: '24px', fontFamily: '"DM Serif Display", serif', marginBottom: '8px' }}>Welcome to Connect</h2>
                <p style={{ color: '#a1a1aa', fontSize: '14px', lineHeight: 1.6 }}>Discover events, join activities, and meet people who share your interests.</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                {[
                  { emoji: '🎯', title: 'Pick your interests', desc: 'We\'ll recommend events you\'ll love' },
                  { emoji: '📍', title: 'Find events nearby', desc: 'Discover what\'s happening in your area' },
                  { emoji: '🤝', title: 'Connect with people', desc: 'Meet others at events you attend' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '14px', padding: '16px', background: '#18181b', borderRadius: '14px', border: '1px solid #27272a' }}>
                    <span style={{ fontSize: '24px' }}>{item.emoji}</span>
                    <div>
                      <p style={{ color: '#fafafa', fontSize: '14px', fontWeight: 600, margin: '0 0 2px' }}>{item.title}</p>
                      <p style={{ color: '#71717a', fontSize: '12px', margin: 0 }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Sync from Spark */}
              {!syncedFromSpark && (
                <div style={{ padding: '16px', background: 'rgba(45,212,191,0.06)', border: '1px solid rgba(45,212,191,0.2)', borderRadius: '14px', marginBottom: '16px' }}>
                  <p style={{ color: '#fafafa', fontSize: '14px', fontWeight: 600, margin: '0 0 6px' }}>
                    {hasSparkProfile ? '🔗 We found your Spark profile!' : '🔗 Already use Spark?'}
                  </p>
                  <p style={{ color: '#a1a1aa', fontSize: '12px', margin: '0 0 12px' }}>
                    {hasSparkProfile
                      ? 'Import your photos, bio, and interests — you\'ll skip straight to Connect.'
                      : 'If you already have a Narbodica account from Spark, sync your profile to skip setup.'}
                  </p>
                  <button onClick={syncFromSpark} disabled={saving}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)', color: '#000', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.6 : 1 }}>
                    {saving ? 'Syncing...' : 'Sync from Spark'}
                  </button>
                </div>
              )}
              {syncedFromSpark && (
                <div style={{ padding: '12px 16px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✅</span><p style={{ color: '#22c55e', fontSize: '13px', margin: 0, fontWeight: 600 }}>Profile synced from Spark</p>
                </div>
              )}
            </div>
          )}

          {/* Step 1: Photo */}
          {currentStep === 1 && (
            <div>
              <h2 style={{ color: '#fafafa', fontSize: '22px', fontFamily: '"DM Serif Display", serif', marginBottom: '8px' }}>Add a Photo</h2>
              <p style={{ color: '#a1a1aa', fontSize: '13px', marginBottom: '24px' }}>
                Add a profile photo so people know who you are at events.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                {formData.photo?.url || user?.profilePhoto ? (
                  <div style={{ position: 'relative' }}>
                    <div style={{ width: '160px', height: '160px', borderRadius: '50%', overflow: 'hidden', border: '3px solid #2dd4bf' }}>
                      <img src={formData.photo?.url || user?.profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <label style={{ position: 'absolute', bottom: '4px', right: '4px', width: '40px', height: '40px', borderRadius: '50%', background: '#2dd4bf', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '3px solid #09090b' }}>
                      <Camera size={18} color="#000" />
                      <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={uploading} />
                    </label>
                  </div>
                ) : (
                  <label style={{ width: '160px', height: '160px', borderRadius: '50%', border: '3px dashed #3f3f46', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#18181b' }}>
                    <Camera size={32} color="#71717a" />
                    <span style={{ color: '#71717a', fontSize: '13px', marginTop: '8px' }}>{uploading ? 'Uploading...' : 'Add Photo'}</span>
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={uploading} />
                  </label>
                )}
              </div>
              <p style={{ textAlign: 'center', color: '#52525b', fontSize: '12px' }}>Photo is optional but recommended</p>
            </div>
          )}

          {/* Step 2: Bio */}
          {currentStep === 2 && (
            <div>
              <h2 style={{ color: '#fafafa', fontSize: '22px', fontFamily: '"DM Serif Display", serif', marginBottom: '8px' }}>About You</h2>
              <p style={{ color: '#a1a1aa', fontSize: '13px', marginBottom: '20px' }}>A short intro so people at events can get to know you. Minimum 10 characters.</p>
              <textarea
                value={formData.bio}
                onChange={e => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="e.g. I'm into live music, hiking, and trying every café in town. Always up for a board game night or outdoor adventure..."
                maxLength={300}
                style={{ width: '100%', minHeight: '140px', padding: '16px', background: '#18181b', border: '1px solid #27272a', borderRadius: '14px', color: '#fafafa', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5 }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                <span style={{ color: formData.bio.length >= 10 ? '#2dd4bf' : '#71717a', fontSize: '12px' }}>
                  {formData.bio.length >= 10 ? '✓ Looks good!' : `${10 - formData.bio.length} more characters needed`}
                </span>
                <span style={{ color: '#52525b', fontSize: '12px' }}>{formData.bio.length}/300</span>
              </div>
            </div>
          )}

          {/* Step 3: Interests */}
          {currentStep === 3 && (
            <div>
              <h2 style={{ color: '#fafafa', fontSize: '22px', fontFamily: '"DM Serif Display", serif', marginBottom: '8px' }}>Your Interests</h2>
              <p style={{ color: '#a1a1aa', fontSize: '13px', marginBottom: '4px' }}>Pick at least 3 interests so we can recommend events you'll love.</p>
              <p style={{ color: '#2dd4bf', fontSize: '13px', fontWeight: 600, marginBottom: '16px' }}>
                {formData.interests.length} selected {formData.interests.length < 3 ? `(need ${3 - formData.interests.length} more)` : '✓'}
              </p>

              {/* Search */}
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <MagnifyingGlass size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#52525b' }} />
                <input type="text" placeholder="Search interests..." value={interestSearch}
                  onChange={e => { setInterestSearch(e.target.value); setActiveGroupTab(null); }}
                  style={{ width: '100%', padding: '12px 14px 12px 40px', background: '#18181b', border: '1px solid #27272a', borderRadius: '10px', color: '#fafafa', fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                />
                {interestSearch && (
                  <button onClick={() => setInterestSearch('')} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: '18px' }}>×</button>
                )}
              </div>

              {interestSearch.length >= 2 ? (
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {searchResults.length === 0 ? (
                    <p style={{ color: '#52525b', textAlign: 'center', padding: '20px', fontSize: '13px' }}>No results for "{interestSearch}"</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {searchResults.map(({ interest, groupId, group }) => {
                        const sel = formData.interests.includes(interest);
                        return (
                          <button key={interest} onClick={() => toggleInterest(interest, groupId)}
                            style={{ padding: '8px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: 'none', fontFamily: 'inherit', background: sel ? group.bg : '#18181b', color: sel ? group.color : '#a1a1aa' }}>
                            {interest}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                    {Object.entries(INTERESTS_TAXONOMY).map(([gId, group]) => {
                      const Icon = group.icon;
                      const isAct = activeGroupTab === gId;
                      const cnt = formData.interests.filter(i => group.interests.includes(i)).length;
                      return (
                        <button key={gId} onClick={() => setActiveGroupTab(isAct ? null : gId)}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 600, fontFamily: 'inherit', background: isAct ? group.bg : '#18181b', color: isAct ? group.color : '#71717a' }}>
                          <Icon size={12} />
                          {group.label.split(' ')[0]}
                          {cnt > 0 && <span style={{ background: group.color, color: '#000', borderRadius: '10px', padding: '0 5px', fontSize: '10px', fontWeight: 700 }}>{cnt}</span>}
                        </button>
                      );
                    })}
                  </div>
                  {activeGroup && (
                    <div style={{ maxHeight: '250px', overflowY: 'auto', padding: '4px 0' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {activeGroup.interests.map(interest => {
                          const sel = formData.interests.includes(interest);
                          return (
                            <button key={interest} onClick={() => toggleInterest(interest, activeGroupTab)}
                              style={{ padding: '8px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: 'none', fontFamily: 'inherit', background: sel ? activeGroup.bg : '#18181b', color: sel ? activeGroup.color : '#a1a1aa' }}>
                              {sel ? '✓ ' : ''}{interest}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!activeGroup && formData.interests.length > 0 && (
                    <div style={{ padding: '12px', background: '#18181b', borderRadius: '10px', marginTop: '4px' }}>
                      <p style={{ color: '#71717a', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Selected</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {formData.interests.slice(0, 15).map(interest => {
                          const g = Object.entries(INTERESTS_TAXONOMY).find(([, gr]) => gr.interests.includes(interest));
                          return (
                            <span key={interest} style={{ padding: '4px 10px', borderRadius: '10px', background: g ? g[1].bg : '#27272a', color: g ? g[1].color : '#a1a1aa', fontSize: '11px', fontWeight: 500 }}>
                              {interest}
                            </span>
                          );
                        })}
                        {formData.interests.length > 15 && <span style={{ padding: '4px 10px', borderRadius: '10px', background: '#27272a', color: '#71717a', fontSize: '11px' }}>+{formData.interests.length - 15} more</span>}
                      </div>
                    </div>
                  )}
                  {!activeGroup && formData.interests.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#52525b' }}>
                      <p style={{ fontSize: '13px' }}>Tap a category above to browse interests</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 4: Location & Event Preferences */}
          {currentStep === 4 && (
            <div>
              <h2 style={{ color: '#fafafa', fontSize: '22px', fontFamily: '"DM Serif Display", serif', marginBottom: '8px' }}>Event Preferences</h2>
              <p style={{ color: '#a1a1aa', fontSize: '13px', marginBottom: '24px' }}>Help us show you the best events in your area.</p>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#a1a1aa', fontSize: '12px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Your Location</label>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                  <input type="text" placeholder="City" value={formData.location.city}
                    onChange={e => setFormData(prev => ({ ...prev, location: { ...prev.location, city: e.target.value } }))}
                    style={{ ...inputStyle, boxSizing: 'border-box' }} />
                  <select value={formData.location.state}
                    onChange={e => setFormData(prev => ({ ...prev, location: { ...prev.location, state: e.target.value } }))}
                    style={{ ...inputStyle }}>
                    <option value="">State</option>
                    {['QLD','NSW','VIC','SA','WA','TAS','NT','ACT'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#a1a1aa', fontSize: '12px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  Max Event Distance: {formData.eventPreferences.maxDistance} km
                </label>
                <input type="range" min="5" max="200" step="5" value={formData.eventPreferences.maxDistance}
                  onChange={e => setFormData(prev => ({ ...prev, eventPreferences: { ...prev.eventPreferences, maxDistance: parseInt(e.target.value) } }))}
                  style={{ width: '100%', accentColor: '#2dd4bf' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ color: '#52525b', fontSize: '11px' }}>5 km</span>
                  <span style={{ color: '#52525b', fontSize: '11px' }}>200 km</span>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#a1a1aa', fontSize: '12px', fontWeight: 600, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  Favourite Event Types (optional)
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {Object.entries(CATEGORY_CONFIG).filter(([k]) => k !== 'Other').map(([cat, config]) => {
                    const Icon = config.icon;
                    const sel = formData.eventPreferences.preferredCategories.includes(cat);
                    return (
                      <button key={cat} onClick={() => toggleCategory(cat)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: 'inherit', background: sel ? config.bg : '#18181b', color: sel ? config.color : '#71717a' }}>
                        <Icon size={13} /> {cat.split(' ')[0]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {currentStep === 5 && (
            <div style={{ textAlign: 'center', paddingTop: '20px' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '36px' }}>🎉</div>
              <h2 style={{ color: '#fafafa', fontSize: '24px', fontFamily: '"DM Serif Display", serif', marginBottom: '8px' }}>You're All Set!</h2>
              <p style={{ color: '#a1a1aa', fontSize: '14px', lineHeight: 1.6, marginBottom: '28px' }}>Your Connect profile is ready. Start discovering events near you.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left' }}>
                {formData.interests.length > 0 && (
                  <div style={{ padding: '14px', background: '#18181b', borderRadius: '12px', border: '1px solid #27272a' }}>
                    <p style={{ color: '#2dd4bf', fontSize: '12px', fontWeight: 600, margin: '0 0 8px' }}>🎯 {formData.interests.length} interests selected</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {formData.interests.slice(0, 8).map(i => <span key={i} style={{ padding: '3px 8px', borderRadius: '8px', background: '#27272a', color: '#a1a1aa', fontSize: '11px' }}>{i}</span>)}
                      {formData.interests.length > 8 && <span style={{ padding: '3px 8px', borderRadius: '8px', background: '#27272a', color: '#52525b', fontSize: '11px' }}>+{formData.interests.length - 8}</span>}
                    </div>
                  </div>
                )}
                {formData.location.city && (
                  <div style={{ padding: '14px', background: '#18181b', borderRadius: '12px', border: '1px solid #27272a' }}>
                    <p style={{ color: '#22d3ee', fontSize: '12px', fontWeight: 600, margin: 0 }}>📍 {formData.location.city}, {formData.location.state}</p>
                  </div>
                )}
                <div style={{ padding: '14px', background: '#18181b', borderRadius: '12px', border: '1px solid #27272a' }}>
                  <p style={{ color: '#71717a', fontSize: '12px', margin: 0 }}>🔍 Showing events within {formData.eventPreferences.maxDistance}km</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ margin: '0 20px 12px', padding: '12px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '10px', color: '#f87171', fontSize: '13px' }}>{error}</div>
        )}

        {/* Navigation */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid #1c1c1f' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            {currentStep > 0 && currentStep < steps.length - 1 && (
              <button onClick={prevStep}
                style={{ flex: 1, padding: '14px', background: '#18181b', border: '1px solid #27272a', borderRadius: '12px', color: '#a1a1aa', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <CaretLeft size={18} /> Back
              </button>
            )}
            <button onClick={nextStep} disabled={!canProceed() || saving}
              style={{
                flex: 2, padding: '14px',
                background: canProceed() ? 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)' : '#27272a',
                border: 'none', borderRadius: '12px',
                color: canProceed() ? '#000' : '#52525b',
                fontWeight: 700, fontSize: '15px',
                cursor: canProceed() ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit', opacity: saving ? 0.6 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
              }}>
              {saving ? 'Saving...' : currentStep === 0 ? 'Get Started' : currentStep === steps.length - 1 ? 'Start Exploring' : currentStep === 4 ? 'Save & Finish' : 'Continue'}
              {currentStep < steps.length - 1 && currentStep > 0 && !saving && <CaretRight size={18} />}
            </button>
          </div>
          {currentStep < steps.length - 1 && (
            <button onClick={() => {
              // Mark onboarding complete and skip
              api('/api/connect/profile', { method: 'PUT', body: JSON.stringify({ onboardingComplete: true }) }).catch(() => {});
              onComplete();
            }}
              style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: '#52525b', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', marginTop: '8px' }}>
              Skip setup for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// SETTINGS VIEW (mirrors Spark's settings)
// ============================================
function SettingsView({ user, onBack, onLogout, api, showMessage, onSetupProfile, onAccessibility }) {
  const [expanded, setExpanded] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfaEnabled || false);
  const [togglingMfa, setTogglingMfa] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const toggle = (section) => setExpanded(prev => ({ ...prev, [section]: !prev[section] }));

  const sStyle = {
    background: '#18181b', borderRadius: '14px',
    border: '1px solid #27272a', marginBottom: '10px', overflow: 'hidden'
  };
  const sHeader = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', cursor: 'pointer', background: 'transparent',
    border: 'none', width: '100%', color: '#fafafa', fontSize: '15px',
    fontWeight: 600, fontFamily: 'inherit'
  };
  const sContent = { padding: '0 20px 16px', borderTop: '1px solid #27272a' };
  const sInput = {
    width: '100%', padding: '12px 16px', borderRadius: '10px',
    border: '1px solid #3f3f46', background: '#09090b', color: '#fafafa',
    fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
  };
  const sBtn = {
    padding: '10px 20px', borderRadius: '10px', border: 'none',
    fontWeight: 600, fontSize: '14px', cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.2s'
  };

  const handleChangePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) { showMessage('Passwords do not match', 'error'); return; }
    if (passwordForm.new.length < 8) { showMessage('Password must be at least 8 characters', 'error'); return; }
    setChangingPassword(true);
    try {
      await api('/api/auth/change-password', {
        method: 'PUT', body: JSON.stringify({ currentPassword: passwordForm.current, newPassword: passwordForm.new })
      });
      showMessage('Password updated successfully ✓');
      setPasswordForm({ current: '', new: '', confirm: '' });
      setExpanded(prev => ({ ...prev, security: false }));
    } catch (err) { showMessage(err.message || 'Failed to change password', 'error'); }
    setChangingPassword(false);
  };

  const handleToggleMfa = async () => {
    setTogglingMfa(true);
    try {
      await api(mfaEnabled ? '/api/auth/mfa/disable' : '/api/auth/mfa/enable', { method: 'POST' });
      setMfaEnabled(!mfaEnabled);
      showMessage(mfaEnabled ? 'MFA disabled' : 'MFA enabled ✓');
    } catch (err) { showMessage(err.message || 'Failed to update MFA', 'error'); }
    setTogglingMfa(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') { showMessage('Please type DELETE to confirm', 'error'); return; }
    setDeletingAccount(true);
    try {
      await api('/api/auth/delete-account', { method: 'DELETE' });
      showMessage('Account deleted');
      onLogout();
    } catch (err) { showMessage(err.message || 'Failed to delete account', 'error'); }
    setDeletingAccount(false);
  };

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', padding: '0 4px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: '4px', display: 'flex' }}>
          <CaretLeft size={20} />
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontFamily: '"DM Serif Display", serif', color: '#fafafa' }}>Settings</h2>
          <p style={{ margin: 0, fontSize: '13px', color: '#71717a' }}>App preferences & security</p>
        </div>
      </div>

      {/* ── Narbodica Profile ── */}
      <div style={{ ...sStyle, border: '1px solid #2dd4bf33', background: 'rgba(45,212,191,0.03)' }}>
        <button onClick={() => toggle('narbodica_profile')} style={{ ...sHeader, color: '#2dd4bf' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>👤</span> Your Narbodica Profile</span>
          <span style={{ color: '#71717a', fontSize: '12px' }}>{expanded.narbodica_profile ? '▲' : '▼'}</span>
        </button>
        {expanded.narbodica_profile && (
          <div style={sContent}>
            <p style={{ color: '#a1a1aa', fontSize: '13px', margin: '12px 0 12px' }}>
              Your Narbodica profile is shared across all Narbodica apps. Location, interests, photos and more — entered once, available everywhere.
            </p>
            <button
              onClick={onSetupProfile}
              style={{ ...sBtn, background: '#2dd4bf', color: '#000', width: '100%', fontWeight: 600 }}>
              Complete your Narbodica profile
            </button>
          </div>
        )}
      </div>

      {/* ── Accessibility ── */}
      <div style={sStyle}>
        <button onClick={onAccessibility} style={sHeader} aria-label="Accessibility settings">
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>♿</span> Accessibility</span>
          <span style={{ color: '#71717a', fontSize: '12px' }}>▶</span>
        </button>
      </div>

      {/* ── Appearance ── */}
      <div style={sStyle}>
        <button onClick={() => toggle('appearance')} style={sHeader}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>🎨</span> Appearance</span>
          <span style={{ color: '#71717a', fontSize: '12px' }}>{expanded.appearance ? '▲' : '▼'}</span>
        </button>
        {expanded.appearance && (
          <div style={sContent}>
            <p style={{ color: '#a1a1aa', fontSize: '13px', margin: '12px 0 8px' }}>Connect uses the Narbodica dark theme across all devices.</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#09090b', borderRadius: '10px' }}>
              <span>🖤</span>
              <div>
                <span style={{ color: '#fafafa', fontWeight: 600, fontSize: '14px' }}>AMOLED Dark</span>
                <span style={{ color: '#71717a', fontSize: '12px', marginLeft: '8px' }}>True black, saves battery</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Security (shared across apps) ── */}
      <div style={sStyle}>
        <button onClick={() => toggle('security')} style={sHeader}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>🔒</span> Security</span>
          <span style={{ color: '#71717a', fontSize: '12px' }}>{expanded.security ? '▲' : '▼'}</span>
        </button>
        {expanded.security && (
          <div style={sContent}>
            <div style={{ marginTop: '12px' }}>
              <h4 style={{ color: '#fafafa', fontSize: '14px', margin: '0 0 12px', fontWeight: 600 }}>Change Password</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ position: 'relative' }}>
                  <input type={showCurrentPw ? 'text' : 'password'} placeholder="Current password" value={passwordForm.current}
                    onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))} style={sInput} />
                  <button onClick={() => setShowCurrentPw(!showCurrentPw)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#71717a', cursor: 'pointer' }}>
                    <Eye size={16} />
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input type={showNewPw ? 'text' : 'password'} placeholder="New password" value={passwordForm.new}
                    onChange={e => setPasswordForm(p => ({ ...p, new: e.target.value }))} style={sInput} />
                  <button onClick={() => setShowNewPw(!showNewPw)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#71717a', cursor: 'pointer' }}>
                    <Eye size={16} />
                  </button>
                </div>
                <input type="password" placeholder="Confirm new password" value={passwordForm.confirm}
                  onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} style={sInput} />
                <button onClick={handleChangePassword} disabled={changingPassword || !passwordForm.current || !passwordForm.new}
                  style={{ ...sBtn, background: '#2dd4bf', color: '#000', opacity: (changingPassword || !passwordForm.current || !passwordForm.new) ? 0.5 : 1 }}>
                  {changingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #27272a' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h4 style={{ color: '#fafafa', fontSize: '14px', margin: '0 0 4px', fontWeight: 600 }}>Two-Factor Authentication</h4>
                  <p style={{ color: '#71717a', fontSize: '12px', margin: 0 }}>{mfaEnabled ? 'Your account is protected with MFA' : 'Add an extra layer of security'}</p>
                </div>
                <button onClick={handleToggleMfa} disabled={togglingMfa}
                  style={{ ...sBtn, padding: '8px 16px', fontSize: '13px', background: mfaEnabled ? '#27272a' : '#2dd4bf', color: mfaEnabled ? '#a1a1aa' : '#000' }}>
                  {togglingMfa ? '...' : mfaEnabled ? 'Disable' : 'Enable'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Identity Verification (shared across apps) ── */}
      <div style={sStyle}>
        <button onClick={() => toggle('identity')} style={sHeader}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>📍</span> Identity Verification
            {user?.isIdentityVerified && (
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: '50%', background: '#22c55e', fontSize: '11px' }}>✓</span>
            )}
          </span>
          <span style={{ color: '#71717a', fontSize: '12px' }}>{expanded.identity ? '▲' : '▼'}</span>
        </button>
        {expanded.identity && (
          <div style={sContent}>
            <div style={{ marginTop: '12px' }}>
              {user?.isIdentityVerified ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', background: 'rgba(34,197,94,0.08)', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.2)' }}>
                  <span style={{ fontSize: '20px' }}>✅</span>
                  <div>
                    <p style={{ color: '#22c55e', fontWeight: 600, fontSize: '14px', margin: 0 }}>Verified</p>
                    <p style={{ color: '#71717a', fontSize: '12px', margin: '2px 0 0' }}>Your identity is verified across all Narbodica apps</p>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ color: '#a1a1aa', fontSize: '13px', margin: '0 0 12px' }}>Verify your identity once — it applies across Spark, Connect, and all Narbodica apps.</p>
                  <button onClick={() => window.open('https://spark.narbodica.com.au', '_blank')}
                    style={{ ...sBtn, background: '#2dd4bf', color: '#000', width: '100%' }}>
                    Verify via Spark
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Notifications (Connect-specific) ── */}
      <div style={sStyle}>
        <button onClick={() => toggle('notifications')} style={sHeader}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>🔔</span> Notifications</span>
          <span style={{ color: '#71717a', fontSize: '12px' }}>{expanded.notifications ? '▲' : '▼'}</span>
        </button>
        {expanded.notifications && (
          <div style={sContent}>
            <p style={{ color: '#a1a1aa', fontSize: '13px', margin: '12px 0' }}>Notification preferences for Connect events and activities.</p>
            {[
              { label: 'Event reminders', desc: 'Get notified before events you\'re attending' },
              { label: 'New events nearby', desc: 'Events matching your interests in your area' },
              { label: 'RSVP updates', desc: 'When people join events you\'re attending' },
              { label: 'Organizer messages', desc: 'Updates from event organizers' }
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: i < 3 ? '1px solid #27272a' : 'none' }}>
                <div>
                  <p style={{ color: '#fafafa', fontSize: '14px', margin: 0, fontWeight: 500 }}>{item.label}</p>
                  <p style={{ color: '#71717a', fontSize: '12px', margin: '2px 0 0' }}>{item.desc}</p>
                </div>
                <span style={{ color: '#2dd4bf', fontSize: '12px', fontWeight: 600 }}>On</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Subscription (shared across apps) ── */}
      <div style={{ ...sStyle, border: '1px solid #2dd4bf22' }}>
        <button onClick={() => toggle('subscription')} style={sHeader}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>👑</span> Subscription
            {user?.subscription?.status === 'active' && (
              <span style={{ background: '#22c55e', color: '#fff', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 700 }}>Active</span>
            )}
            {user?.subscription?.status === 'trial' && (
              <span style={{ background: '#f59e0b', color: '#fff', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 700 }}>Trial</span>
            )}
          </span>
          <span style={{ color: '#71717a', fontSize: '12px' }}>{expanded.subscription ? '▲' : '▼'}</span>
        </button>
        {expanded.subscription && (
          <SubscriptionSection user={user} api={api} showMessage={showMessage} sBtn={sBtn} />
        )}
      </div>

      {/* ── About Connect ── */}
      <div style={sStyle}>
        <button onClick={() => toggle('about')} style={sHeader}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>🟢</span> About Connect</span>
          <span style={{ color: '#71717a', fontSize: '12px' }}>{expanded.about ? '▲' : '▼'}</span>
        </button>
        {expanded.about && (
          <div style={sContent}>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'Version', value: 'v1.0.0' },
                { label: 'Platform', value: 'Narbodica App Suite' },
                { label: 'Developer', value: 'Narbodica Pty Ltd' }
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 2 ? '1px solid #27272a' : 'none' }}>
                  <span style={{ color: '#a1a1aa', fontSize: '13px' }}>{item.label}</span>
                  <span style={{ color: '#fafafa', fontSize: '13px', fontWeight: 500 }}>{item.value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <a href="https://narbodica.com.au/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#2dd4bf', fontSize: '13px', textDecoration: 'none' }}>Privacy Policy</a>
                <span style={{ color: '#3f3f46' }}>·</span>
                <a href="https://narbodica.com.au/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#2dd4bf', fontSize: '13px', textDecoration: 'none' }}>Terms of Service</a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Delete Account (shared across apps) ── */}
      <div style={{ ...sStyle, border: '1px solid rgba(239,68,68,0.3)' }}>
        <button onClick={() => toggle('delete')} style={{ ...sHeader, color: '#ef4444' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>⚠️</span> Delete Account</span>
          <span style={{ color: '#ef4444', fontSize: '12px' }}>{expanded.delete ? '▲' : '▼'}</span>
        </button>
        {expanded.delete && (
          <div style={{ ...sContent, borderTop: '1px solid rgba(239,68,68,0.2)' }}>
            <div style={{ marginTop: '12px', padding: '14px 16px', background: 'rgba(239,68,68,0.06)', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.15)' }}>
              <p style={{ color: '#fca5a5', fontSize: '13px', margin: '0 0 4px', fontWeight: 600 }}>This action is permanent</p>
              <p style={{ color: '#a1a1aa', fontSize: '12px', margin: '0 0 12px' }}>
                Deleting your account removes all your data across all Narbodica apps — Spark, Connect, and any future apps. This cannot be undone.
              </p>
              <input type="text" placeholder='Type "DELETE" to confirm' value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                style={{ ...sInput, borderColor: 'rgba(239,68,68,0.3)', marginBottom: '10px' }} />
              <button onClick={handleDeleteAccount} disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
                style={{ ...sBtn, background: deleteConfirmText === 'DELETE' ? '#ef4444' : '#27272a', color: deleteConfirmText === 'DELETE' ? '#fff' : '#71717a', width: '100%', opacity: deletingAccount ? 0.5 : 1 }}>
                {deletingAccount ? 'Deleting...' : 'Permanently Delete Account'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Log Out ── */}
      <button onClick={onLogout} style={{
        width: '100%', padding: '14px', borderRadius: '14px', border: '1px solid #27272a',
        background: '#18181b', color: '#ef4444', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
        fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '10px'
      }}>
        <SignOut size={16} /> Log Out
      </button>

      <p style={{ textAlign: 'center', color: '#3f3f46', fontSize: '12px', marginTop: '20px' }}>
        Connect by Narbodica · v1.0.0<br />Same account across all Narbodica apps
      </p>
    </div>
  );
}

// ============================================
// SUBSCRIPTION SECTION (shared Narbodica subscription)
// ============================================
function SubscriptionSection({ user, api, showMessage, sBtn }) {
  const [loading, setLoading] = useState(false);
  const [subData, setSubData] = useState(user?.subscription || null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const sub = subData || user?.subscription;
  const isActive = sub?.status === 'active';
  const isTrial = sub?.status === 'trial';
  const isCancelled = sub?.status === 'cancelled';
  const isPastDue = sub?.status === 'past_due';
  const isValid = sub?.isValid;
  const daysRemaining = sub?.daysRemaining || 0;

  const sContent = { padding: '0 20px 16px', borderTop: '1px solid #27272a' };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const data = await api('/api/subscription/create-checkout', { method: 'POST' });
      if (data.data?.checkoutUrl) {
        window.location.href = data.data.checkoutUrl;
      } else {
        showMessage(data.message || 'Failed to start checkout', 'error');
      }
    } catch (err) {
      showMessage(err.message || 'Failed to start checkout', 'error');
    }
    setLoading(false);
  };

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const data = await api('/api/subscription/billing-portal', { method: 'POST' });
      if (data.data?.portalUrl) {
        window.location.href = data.data.portalUrl;
      } else {
        showMessage(data.message || 'Failed to open billing portal', 'error');
      }
    } catch (err) {
      showMessage(err.message || 'Failed to open billing portal', 'error');
    }
    setLoading(false);
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      const data = await api('/api/subscription/cancel', {
        method: 'POST',
        body: JSON.stringify({ reason: cancelReason })
      });
      showMessage(data.message || 'Subscription cancelled');
      setShowCancelConfirm(false);
      setSubData(data.data?.subscription || { ...sub, status: 'cancelled' });
    } catch (err) {
      showMessage(err.message || 'Failed to cancel', 'error');
    }
    setLoading(false);
  };

  // Active / Trial view
  if (isValid) {
    return (
      <div style={sContent}>
        <div style={{ marginTop: '12px' }}>
          {/* Status card */}
          <div style={{ padding: '14px 16px', background: '#09090b', borderRadius: '10px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <span style={{ fontSize: '18px' }}>{isTrial ? '⏳' : '👑'}</span>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#fafafa', fontWeight: 600, fontSize: '14px', margin: 0 }}>
                  {isTrial ? 'Free Trial' : isCancelled ? 'Subscription Ending' : isPastDue ? 'Payment Issue' : 'Narbodica Member'}
                </p>
                <p style={{ color: '#71717a', fontSize: '12px', margin: '2px 0 0' }}>
                  {isTrial ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`
                    : isCancelled ? `Access until ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`
                    : isPastDue ? 'Please update your payment method'
                    : `$25/month · Renews ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`}
                </p>
              </div>
            </div>
            {isTrial && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ background: '#27272a', borderRadius: '4px', height: '4px', overflow: 'hidden' }}>
                  <div style={{
                    background: daysRemaining <= 2 ? '#ef4444' : '#2dd4bf',
                    height: '100%', borderRadius: '4px',
                    width: `${Math.max(5, (daysRemaining / 7) * 100)}%`
                  }} />
                </div>
              </div>
            )}
          </div>

          {/* Apps included */}
          <div style={{ padding: '12px 16px', background: '#09090b', borderRadius: '10px', marginBottom: '12px' }}>
            <p style={{ color: '#71717a', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 10px' }}>Your Narbodica Apps</p>
            {[
              { name: 'Maia', desc: 'Your AI companion', icon: '🤖', active: true },
              { name: 'Spark', desc: 'Dating & connections', icon: '💜', active: true },
              { name: 'Connect', desc: 'Events & activities', icon: '🤝', active: true },
              { name: 'Signal', desc: 'Community chat', icon: '💬', active: true },
              { name: 'Marketplace', desc: 'Buy & sell', icon: '🛒', active: true },
              { name: 'Grassroots', desc: 'Community action', icon: '✊', active: true },
              { name: 'Careers', desc: 'Your working life', icon: '💼', active: true },
              { name: 'Eros', desc: 'For couples', icon: '❤️', active: true },
              { name: 'Artemis', desc: 'Find babysitters', icon: '🏛️', active: true },
            ].map((app, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
                <span style={{ fontSize: '14px' }}>{app.icon}</span>
                <div style={{ flex: 1 }}>
                  <span style={{ color: '#fafafa', fontSize: '13px', fontWeight: 600 }}>{app.name}</span>
                  <span style={{ color: '#71717a', fontSize: '11px', marginLeft: '6px' }}>{app.desc}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Subscribe CTA for trial */}
          {isTrial && (
            <button onClick={handleSubscribe} disabled={loading}
              style={{ ...sBtn, width: '100%', background: 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)', color: '#000', padding: '14px', fontSize: '15px', marginBottom: '8px', opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Processing...' : 'Subscribe — $25/month'}
            </button>
          )}

          {/* Past due warning */}
          {isPastDue && (
            <div style={{ padding: '12px', borderRadius: '10px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚠️</span>
              <p style={{ color: '#fbbf24', fontSize: '12px', margin: 0 }}>Your last payment failed. Please update your payment method.</p>
            </div>
          )}

          {/* Manage billing */}
          {(isActive || isCancelled || isPastDue) && sub?.paymentProvider === 'stripe' && (
            <button onClick={handleManageBilling} disabled={loading}
              style={{ ...sBtn, width: '100%', background: '#27272a', color: '#fafafa', padding: '12px', marginBottom: '8px' }}>
              {loading ? 'Opening...' : 'Manage Billing & Payment'}
            </button>
          )}

          {/* Cancel */}
          {isActive && !showCancelConfirm && (
            <button onClick={() => setShowCancelConfirm(true)}
              style={{ ...sBtn, width: '100%', background: 'transparent', border: '1px solid #27272a', color: '#71717a', padding: '10px' }}>
              Cancel Subscription
            </button>
          )}

          {/* Cancelled notice */}
          {isCancelled && !showCancelConfirm && (
            <div style={{ padding: '12px', borderRadius: '10px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚠️</span>
              <p style={{ color: '#fbbf24', fontSize: '12px', margin: 0 }}>
                Cancelled. Access continues until {new Date(sub.currentPeriodEnd).toLocaleDateString()}.
              </p>
            </div>
          )}

          {/* Cancel confirmation */}
          {showCancelConfirm && (
            <div style={{ padding: '14px', borderRadius: '10px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
              <p style={{ color: '#fca5a5', fontWeight: 600, fontSize: '13px', margin: '0 0 8px' }}>Are you sure?</p>
              <p style={{ color: '#a1a1aa', fontSize: '12px', margin: '0 0 10px' }}>
                You'll lose access to all Narbodica apps when your current period ends.
              </p>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                placeholder="Optional: Tell us why..."
                style={{ width: '100%', padding: '10px', background: '#09090b', border: '1px solid #3f3f46', borderRadius: '8px', color: '#fafafa', fontSize: '13px', minHeight: '50px', resize: 'vertical', marginBottom: '10px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setShowCancelConfirm(false)}
                  style={{ ...sBtn, flex: 1, background: '#27272a', color: '#fafafa', padding: '10px' }}>Keep</button>
                <button onClick={handleCancel} disabled={loading}
                  style={{ ...sBtn, flex: 1, background: '#ef4444', color: '#fff', padding: '10px', opacity: loading ? 0.6 : 1 }}>
                  {loading ? 'Cancelling...' : 'Confirm Cancel'}
                </button>
              </div>
            </div>
          )}

          <p style={{ textAlign: 'center', fontSize: '11px', color: '#3f3f46', margin: '12px 0 0' }}>
            One subscription covers all Narbodica apps
          </p>
        </div>
      </div>
    );
  }

  // No subscription / expired — show subscribe CTA
  return (
    <div style={sContent}>
      <div style={{ marginTop: '12px' }}>
        <div style={{ textAlign: 'center', padding: '20px 0 16px' }}>
          <span style={{ fontSize: '32px' }}>👑</span>
          <h3 style={{ color: '#fafafa', fontSize: '18px', margin: '8px 0 4px', fontFamily: '"DM Serif Display", serif' }}>Narbodica</h3>
          <p style={{ color: '#a1a1aa', fontSize: '13px', margin: 0 }}>
            {sub?.trialUsed ? 'Your free trial has ended' : 'Subscribe to unlock all features'}
          </p>
        </div>

        {/* Features */}
        {[
          { icon: '❤️', text: 'Unlimited swipes & likes' },
          { icon: '💬', text: 'Unlimited messaging' },
          { icon: '📅', text: 'AI-powered date booking' },
          { icon: '🛡️', text: 'Identity verified community' },
          { icon: '⚡', text: 'All future Narbodica apps included' }
        ].map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: i < 4 ? '1px solid #1c1c1f' : 'none' }}>
            <span style={{ fontSize: '16px' }}>{f.icon}</span>
            <p style={{ color: '#fafafa', fontSize: '13px', margin: 0, fontWeight: 500 }}>{f.text}</p>
          </div>
        ))}

        {/* Apps */}
        <div style={{ padding: '12px', background: '#09090b', borderRadius: '10px', margin: '16px 0' }}>
          <p style={{ color: '#71717a', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Included Apps</p>
          {[
            { name: 'Spark', desc: 'Dating', active: true },
            { name: 'Connect', desc: 'Events', active: true },
            { name: 'More coming', desc: '', active: false }
          ].map((app, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', opacity: app.active ? 1 : 0.4 }}>
              <span style={{ fontSize: '12px' }}>{app.active ? '✅' : '🔜'}</span>
              <span style={{ color: '#fafafa', fontSize: '12px', fontWeight: 500 }}>{app.name}</span>
              {app.desc && <span style={{ color: '#71717a', fontSize: '11px' }}>· {app.desc}</span>}
            </div>
          ))}
        </div>

        <button onClick={handleSubscribe} disabled={loading}
          style={{
            ...sBtn, width: '100%', padding: '16px', fontSize: '16px',
            background: loading ? '#71717a' : 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)',
            color: '#000', boxShadow: loading ? 'none' : '0 4px 16px rgba(45,212,191,0.3)',
            opacity: loading ? 0.7 : 1
          }}>
          {loading ? 'Processing...' : 'Subscribe — $25/month'}
        </button>

        <p style={{ textAlign: 'center', fontSize: '11px', color: '#3f3f46', margin: '10px 0 0', lineHeight: 1.5 }}>
          Cancel anytime · No lock-in · Prices in AUD
          <br />Powered by Stripe · Auto-renews monthly
        </p>
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function CalendarModal({ events, source, onClose, onSelectEvent, activeCategory }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const monthName = currentMonth.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  // Map events to dates
  const eventsByDate = {};
  events.forEach(event => {
    const d = new Date(event.startDate);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!eventsByDate[key]) eventsByDate[key] = [];
    eventsByDate[key].push(event);
  });

  // Get events for selected date
  const selectedDateEvents = selectedDate
    ? eventsByDate[`${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${selectedDate.getDate()}`] || []
    : [];

  // Build calendar grid
  const cells = [];
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) cells.push(null);
  // Day cells
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
      zIndex: 9998, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      padding: '20px 0', overflow: 'auto'
    }}>
      <div className="fade-in" style={{
        maxWidth: '440px', width: '100%', background: '#09090b',
        borderRadius: '20px', border: '1px solid #27272a', overflow: 'hidden',
        margin: '0 16px'
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #27272a', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Calendar size={20} color="#2dd4bf" />
          <h2 style={{ margin: 0, color: '#fafafa', fontSize: '16px', fontWeight: 700, flex: 1 }}>
            {source === 'discover' ? 'Events Calendar' : 'My Events Calendar'}
          </h2>
          <button onClick={onClose} style={iconBtnStyle}><X size={18} /></button>
        </div>

        {/* Month navigation */}
        <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={prevMonth} style={{ ...iconBtnStyle, width: '32px', height: '32px' }}>
            <CaretLeft size={16} />
          </button>
          <span style={{ color: '#fafafa', fontSize: '15px', fontWeight: 700, fontFamily: '"DM Serif Display", serif' }}>
            {monthName}
          </span>
          <button onClick={nextMonth} style={{ ...iconBtnStyle, width: '32px', height: '32px' }}>
            <CaretRight size={16} />
          </button>
        </div>

        {/* Day labels */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 12px' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} style={{ textAlign: 'center', padding: '6px 0', color: '#71717a', fontSize: '11px', fontWeight: 600 }}>
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 12px 12px', gap: '2px' }}>
          {cells.map((day, i) => {
            if (day === null) return <div key={`empty-${i}`} />;

            const dateKey = `${year}-${month}-${day}`;
            const dayEvents = eventsByDate[dateKey] || [];
            const hasEvents = dayEvents.length > 0;
            const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
            const isSelected = selectedDate &&
              selectedDate.getFullYear() === year &&
              selectedDate.getMonth() === month &&
              selectedDate.getDate() === day;

            // Get unique category colors for dots
            const dotColors = [...new Set(dayEvents.map(e => getCategoryConfig(e.category).color))].slice(0, 3);

            return (
              <button
                key={day}
                onClick={() => setSelectedDate(new Date(year, month, day))}
                style={{
                  width: '100%', aspectRatio: '1', border: 'none', borderRadius: '10px',
                  cursor: hasEvents ? 'pointer' : 'default',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: '2px', fontFamily: 'inherit', fontSize: '13px', fontWeight: isToday ? 700 : 500,
                  background: isSelected ? 'rgba(45,212,191,0.15)' : 'transparent',
                  color: isToday ? '#2dd4bf' : (hasEvents ? '#fafafa' : '#52525b'),
                  transition: 'all 0.15s',
                  outline: isToday && !isSelected ? '1px solid rgba(45,212,191,0.3)' : 'none'
                }}
              >
                {day}
                {hasEvents && (
                  <div style={{ display: 'flex', gap: '2px' }}>
                    {dotColors.map((color, idx) => (
                      <div key={idx} style={{
                        width: '4px', height: '4px', borderRadius: '50%', background: color
                      }} />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Active filters indicator */}
        {source === 'discover' && activeCategory && (
          <div style={{ padding: '0 20px 10px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: getCategoryConfig(activeCategory).bg, padding: '4px 10px', borderRadius: '12px' }}>
              <span style={{ color: getCategoryConfig(activeCategory).color, fontSize: '11px', fontWeight: 600 }}>
                Filtered: {activeCategory}
              </span>
            </div>
          </div>
        )}

        {/* Selected date events */}
        <div style={{ borderTop: '1px solid #27272a', maxHeight: '300px', overflow: 'auto' }}>
          {selectedDate ? (
            <div style={{ padding: '14px 20px' }}>
              <h3 style={{ color: '#a1a1aa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', fontWeight: 600 }}>
                {selectedDate.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
                <span style={{ marginLeft: '6px', color: '#2dd4bf' }}>({selectedDateEvents.length} event{selectedDateEvents.length !== 1 ? 's' : ''})</span>
              </h3>

              {selectedDateEvents.length === 0 ? (
                <p style={{ color: '#52525b', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>
                  No events on this day
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedDateEvents.map(event => {
                    const cat = getCategoryConfig(event.category);
                    const CatIcon = cat.icon;
                    return (
                      <div
                        key={event._id}
                        onClick={() => onSelectEvent(event)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px',
                          background: '#18181b', borderRadius: '12px', cursor: 'pointer',
                          border: '1px solid #27272a', transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#3f3f46'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#27272a'}
                      >
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '10px', background: cat.bg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          <CatIcon size={16} color={cat.color} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#fafafa', fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {event.title}
                          </div>
                          <div style={{ color: '#71717a', fontSize: '11px', marginTop: '2px' }}>
                            {formatTime(event.startDate)} · {event.location?.venueName || event.location?.address?.city || 'Online'}
                          </div>
                        </div>
                        <div style={{ color: event.pricing?.isFree ? '#4ade80' : '#a1a1aa', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>
                          {formatPrice(event.pricing)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '24px 20px', textAlign: 'center', color: '#52525b', fontSize: '13px' }}>
              Tap a date to see events
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EventCard({ event, rsvpStatus, onClick }) {
  const cat = getCategoryConfig(event.category);
  const CatIcon = cat.icon;

  // Time urgency calculation
  const now = Date.now();
  const start = new Date(event.startDate).getTime();
  const end = event.endDate ? new Date(event.endDate).getTime() : start + 3 * 60 * 60 * 1000;
  const minsUntilStart = Math.floor((start - now) / 60000);
  const isHappeningNow = now >= start && now <= end;
  const isStartingSoon = minsUntilStart > 0 && minsUntilStart <= 120; // within 2 hours

  const timeBadge = isHappeningNow
    ? { text: 'Happening now', bg: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)' }
    : isStartingSoon
      ? { text: minsUntilStart <= 30 ? `Starts in ${minsUntilStart}m` : `Starts in ${Math.floor(minsUntilStart / 60)}h ${minsUntilStart % 60}m`, bg: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }
      : null;

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${event.title} — ${event.category}${timeBadge ? ', ' + timeBadge.text : ''}`}
      onKeyDown={e => e.key === 'Enter' && onClick?.()}
      className="fade-in"
      style={{
        background: '#09090b',
        border: isHappeningNow ? '1px solid rgba(74,222,128,0.3)' : isStartingSoon ? '1px solid rgba(251,191,36,0.2)' : '1px solid #27272a',
        borderRadius: '16px', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s'
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
    >
      {/* Cover image or category header */}
      {event.coverImage?.url ? (
        <div style={{ height: '120px', background: `url(${event.coverImage.url}) center/cover`, position: 'relative' }}>
          <div style={{ position: 'absolute', top: '10px', left: '10px', display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', padding: '4px 10px', borderRadius: '20px' }}>
            <CatIcon size={12} color={cat.color} aria-hidden="true" />
            <span style={{ color: '#fafafa', fontSize: '11px', fontWeight: 600 }}>{event.category}</span>
          </div>
          {timeBadge && (
            <div style={{ position: 'absolute', top: '10px', right: '10px', background: timeBadge.bg, border: timeBadge.border, padding: '4px 10px', borderRadius: '20px' }}>
              <span style={{ color: timeBadge.color, fontSize: '10px', fontWeight: 700 }}>{timeBadge.text}</span>
            </div>
          )}
        </div>
      ) : (
        <div style={{ height: '50px', background: cat.bg, position: 'relative', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '8px' }}>
          <CatIcon size={16} color={cat.color} aria-hidden="true" />
          <span style={{ color: cat.color, fontSize: '12px', fontWeight: 600 }}>{event.category}</span>
          {timeBadge && (
            <div style={{ marginLeft: 'auto', background: timeBadge.bg, border: timeBadge.border, padding: '3px 8px', borderRadius: '12px' }}>
              <span style={{ color: timeBadge.color, fontSize: '10px', fontWeight: 700 }}>{timeBadge.text}</span>
            </div>
          )}
        </div>
      )}

      <div style={{ padding: '14px 16px' }}>
        <h3 style={{ color: '#fafafa', fontSize: '15px', fontWeight: 700, margin: '0 0 8px', lineHeight: 1.3 }}>
          {event.title}
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={13} color="#2dd4bf" aria-hidden="true" />
            <span style={{ color: '#a1a1aa', fontSize: '12px' }}>{formatDate(event.startDate)} · {formatTime(event.startDate)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MapPin size={13} color="#71717a" aria-hidden="true" />
            <span style={{ color: '#71717a', fontSize: '12px' }}>{event.location?.venueName || event.location?.address?.city || 'Online'}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: event.pricing?.isFree ? '#4ade80' : '#fafafa', fontSize: '13px', fontWeight: 700 }}>
            {formatPrice(event.pricing)}
          </span>
          <span style={{ color: '#2dd4bf', fontSize: '11px', fontWeight: 600 }}>{timeUntil(event.startDate)}</span>
        </div>
      </div>
    </div>
  );
}

function ProfileView({ user, connectProfile, onSyncSpark, onSetupProfile, onUpdateProfile }) {
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(connectProfile?.interestGroups || []);
  const [selectedInterests, setSelectedInterests] = useState(connectProfile?.interests || []);
  const [activeGroupTab, setActiveGroupTab] = useState(null);
  const [interestSearch, setInterestSearch] = useState('');

  useEffect(() => {
    setSelectedGroups(connectProfile?.interestGroups || []);
    setSelectedInterests(connectProfile?.interests || []);
  }, [connectProfile]);

  const openEditor = () => {
    setSelectedGroups(connectProfile?.interestGroups || []);
    setSelectedInterests(connectProfile?.interests || []);
    setActiveGroupTab(null);
    setInterestSearch('');
    setShowInterestModal(true);
  };

  const toggleGroup = (groupId) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) {
        const groupInterests = INTERESTS_TAXONOMY[groupId].interests;
        setSelectedInterests(si => si.filter(i => !groupInterests.includes(i)));
        return prev.filter(g => g !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const toggleInterest = (interest, groupId) => {
    setSelectedInterests(prev => {
      if (prev.includes(interest)) {
        return prev.filter(i => i !== interest);
      } else {
        if (!selectedGroups.includes(groupId)) {
          setSelectedGroups(sg => [...sg, groupId]);
        }
        return [...prev, interest];
      }
    });
  };

  const selectAllInGroup = (groupId) => {
    const groupInterests = INTERESTS_TAXONOMY[groupId].interests;
    const allSelected = groupInterests.every(i => selectedInterests.includes(i));
    if (allSelected) {
      setSelectedInterests(prev => prev.filter(i => !groupInterests.includes(i)));
    } else {
      setSelectedInterests(prev => [...new Set([...prev, ...groupInterests])]);
      if (!selectedGroups.includes(groupId)) {
        setSelectedGroups(sg => [...sg, groupId]);
      }
    }
  };

  const saveInterests = () => {
    onUpdateProfile({ interestGroups: selectedGroups, interests: selectedInterests });
    setShowInterestModal(false);
  };

  const countForGroup = (groupId) => {
    const groupInterests = INTERESTS_TAXONOMY[groupId].interests;
    return selectedInterests.filter(i => groupInterests.includes(i)).length;
  };

  const totalSelected = selectedInterests.length;

  // Search across all interests
  const searchResults = interestSearch.length >= 2
    ? Object.entries(INTERESTS_TAXONOMY).flatMap(([groupId, group]) =>
        group.interests
          .filter(i => i.toLowerCase().includes(interestSearch.toLowerCase()))
          .map(i => ({ interest: i, groupId, group }))
      ).slice(0, 20)
    : [];

  // Active group data
  const activeGroup = activeGroupTab ? INTERESTS_TAXONOMY[activeGroupTab] : null;
  const taxonomyEntries = Object.entries(INTERESTS_TAXONOMY);

  return (
    <div style={{ padding: '16px 20px' }}>

      {/* ═══════ INTEREST EDITOR MODAL ═══════ */}
      {showInterestModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(8px)',
          zIndex: 9999, display: 'flex', flexDirection: 'column'
        }}>
          <div className="fade-in" style={{
            maxWidth: '440px', width: '100%', margin: '0 auto', display: 'flex',
            flexDirection: 'column', height: '100%', background: '#09090b'
          }}>
            {/* Modal header */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #27272a', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <Heart size={18} color="#fb7185" />
              <h2 style={{ margin: 0, color: '#fafafa', fontSize: '16px', fontWeight: 700, flex: 1 }}>
                Edit Interests
              </h2>
              <span style={{ background: 'rgba(45,212,191,0.15)', color: '#2dd4bf', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 700 }}>
                {totalSelected} selected
              </span>
              <button onClick={() => setShowInterestModal(false)} style={iconBtnStyle}><X size={18} /></button>
            </div>

            {/* Search */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #1c1c1f', flexShrink: 0 }}>
              <div style={{ position: 'relative' }}>
                <MagnifyingGlass size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#71717a' }} />
                <input
                  value={interestSearch}
                  onChange={e => { setInterestSearch(e.target.value); if (e.target.value.length >= 2) setActiveGroupTab(null); }}
                  placeholder="Search 260+ interests..."
                  style={{ ...inputStyle, paddingLeft: '32px', marginBottom: 0, fontSize: '13px', height: '38px' }}
                />
                {interestSearch && (
                  <button onClick={() => setInterestSearch('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#71717a', cursor: 'pointer' }}>
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Search results view */}
            {interestSearch.length >= 2 ? (
              <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
                {searchResults.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#52525b' }}>
                    <MagnifyingGlass size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
                    <p style={{ fontSize: '13px' }}>No matching interests</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {searchResults.map(({ interest, groupId, group }) => {
                      const isSelected = selectedInterests.includes(interest);
                      const Icon = group.icon;
                      return (
                        <button key={`${groupId}-${interest}`} onClick={() => toggleInterest(interest, groupId)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px',
                            borderRadius: '16px', border: isSelected ? `1px solid ${group.color}` : '1px solid #3f3f46',
                            cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                            background: isSelected ? group.bg : 'transparent',
                            color: isSelected ? group.color : '#a1a1aa', transition: 'all 0.15s'
                          }}>
                          <Icon size={12} />
                          {interest}
                          {isSelected && <Check size={11} />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Group tabs — horizontal scrollable */}
                <div style={{ borderBottom: '1px solid #1c1c1f', flexShrink: 0 }}>
                  <div style={{ display: 'flex', overflow: 'auto', padding: '8px 12px', gap: '6px', scrollbarWidth: 'none' }}>
                    <button
                      onClick={() => setActiveGroupTab(null)}
                      style={{
                        padding: '6px 12px', borderRadius: '16px', border: 'none', cursor: 'pointer',
                        fontSize: '11px', fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                        background: !activeGroupTab ? 'rgba(45,212,191,0.15)' : '#18181b',
                        color: !activeGroupTab ? '#2dd4bf' : '#71717a', transition: 'all 0.15s'
                      }}
                    >
                      All Groups
                    </button>
                    {taxonomyEntries.map(([groupId, group]) => {
                      const Icon = group.icon;
                      const count = countForGroup(groupId);
                      const isActive = activeGroupTab === groupId;
                      return (
                        <button key={groupId} onClick={() => setActiveGroupTab(groupId)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            padding: '6px 10px', borderRadius: '16px', border: 'none', cursor: 'pointer',
                            fontSize: '11px', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                            background: isActive ? group.bg : '#18181b',
                            color: isActive ? group.color : '#71717a', transition: 'all 0.15s'
                          }}
                        >
                          <Icon size={12} />
                          {group.label.split(' & ')[0]}
                          {count > 0 && <span style={{ background: group.color + '30', color: group.color, padding: '1px 5px', borderRadius: '8px', fontSize: '10px', fontWeight: 700 }}>{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Content area */}
                <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
                  {!activeGroupTab ? (
                    /* All Groups overview */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {taxonomyEntries.map(([groupId, group]) => {
                        const Icon = group.icon;
                        const isGroupSelected = selectedGroups.includes(groupId);
                        const count = countForGroup(groupId);
                        return (
                          <div key={groupId}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                              background: isGroupSelected ? group.bg : '#18181b', borderRadius: '12px',
                              border: `1px solid ${isGroupSelected ? group.color + '40' : '#27272a'}`,
                              cursor: 'pointer', transition: 'all 0.15s'
                            }}
                            onClick={() => setActiveGroupTab(groupId)}
                          >
                            <div style={{
                              width: '34px', height: '34px', borderRadius: '9px',
                              background: isGroupSelected ? group.color + '25' : '#27272a',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                              <Icon size={16} color={isGroupSelected ? group.color : '#71717a'} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: isGroupSelected ? group.color : '#d4d4d8', fontSize: '13px', fontWeight: 600 }}>
                                {group.label}
                              </div>
                              <div style={{ color: '#52525b', fontSize: '11px' }}>
                                {group.interests.length} interests{count > 0 && <span style={{ color: group.color }}> · {count} picked</span>}
                              </div>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleGroup(groupId); }}
                              style={{
                                width: '24px', height: '24px', borderRadius: '6px', border: 'none',
                                background: isGroupSelected ? group.color : '#27272a',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', flexShrink: 0
                              }}
                            >
                              {isGroupSelected && <Check size={14} color="#000" />}
                            </button>
                            <CaretRight size={14} color="#52525b" style={{ flexShrink: 0 }} />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* Single group detail */
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                        <button onClick={() => setActiveGroupTab(null)} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: '4px' }}>
                          <ArrowLeft size={16} />
                        </button>
                        {(() => { const Icon = activeGroup.icon; return <Icon size={18} color={activeGroup.color} />; })()}
                        <h3 style={{ margin: 0, color: activeGroup.color, fontSize: '15px', fontWeight: 700, flex: 1 }}>{activeGroup.label}</h3>
                        <button
                          onClick={() => selectAllInGroup(activeGroupTab)}
                          style={{
                            padding: '5px 10px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                            fontSize: '11px', fontWeight: 600, fontFamily: 'inherit',
                            background: 'rgba(45,212,191,0.1)', color: '#2dd4bf'
                          }}
                        >
                          {activeGroup.interests.every(i => selectedInterests.includes(i)) ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {activeGroup.interests.map(interest => {
                          const isSelected = selectedInterests.includes(interest);
                          return (
                            <button key={interest} onClick={() => toggleInterest(interest, activeGroupTab)}
                              style={{
                                padding: '8px 14px', borderRadius: '16px',
                                border: isSelected ? `1px solid ${activeGroup.color}` : '1px solid #3f3f46',
                                cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: 'inherit',
                                background: isSelected ? activeGroup.bg : 'transparent',
                                color: isSelected ? activeGroup.color : '#a1a1aa',
                                transition: 'all 0.12s'
                              }}>
                              {interest}{isSelected ? ' ✓' : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Save footer */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid #27272a', flexShrink: 0 }}>
              <button onClick={saveInterests} style={{ ...primaryButtonStyle, width: '100%' }}>
                Save — {selectedGroups.length} groups, {totalSelected} interests
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ PROFILE PAGE ═══════ */}

      {/* Profile Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(45,212,191,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <User size={28} color="#2dd4bf" />
        </div>
        <div>
          <h2 style={{ margin: 0, color: '#fafafa', fontSize: '18px', fontWeight: 700 }}>{user?.name}</h2>
          <p style={{ margin: 0, color: '#71717a', fontSize: '13px' }}>{user?.email}</p>
        </div>
      </div>

      {/* Stats */}
      {connectProfile?.stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '20px' }}>
          {[
            { label: 'Attended', value: connectProfile.stats.eventsAttended },
            { label: 'Interested', value: connectProfile.stats.eventsInterested },
            { label: 'Created', value: connectProfile.stats.eventsCreated }
          ].map(stat => (
            <div key={stat.label} style={{ background: '#18181b', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
              <div style={{ color: '#fafafa', fontSize: '20px', fontWeight: 700 }}>{stat.value}</div>
              <div style={{ color: '#71717a', fontSize: '11px', marginTop: '2px' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Narbodica Profile */}
      <div style={{ background: '#18181b', border: '1px solid #2dd4bf33', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={{ fontSize: '16px' }}>👤</span>
          <span style={{ color: '#2dd4bf', fontSize: '14px', fontWeight: 600 }}>Your Narbodica Profile</span>
        </div>
        <p style={{ color: '#71717a', fontSize: '13px', marginBottom: '12px' }}>
          Your profile is shared across all Narbodica apps. Update it once and it applies everywhere.
        </p>
        <button onClick={onSetupProfile} style={{ ...primaryButtonStyle, padding: '10px 16px', fontSize: '13px', background: 'rgba(45,212,191,0.12)', color: '#2dd4bf' }}>
          Complete your Narbodica profile
        </button>
      </div>

      {/* Interests — display & edit button */}
      <div style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Heart size={16} color="#fb7185" />
            <span style={{ color: '#fafafa', fontSize: '14px', fontWeight: 600 }}>Your Interests</span>
            {(connectProfile?.interests?.length || 0) > 0 && (
              <span style={{ background: 'rgba(45,212,191,0.15)', color: '#2dd4bf', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700 }}>
                {connectProfile.interests.length}
              </span>
            )}
          </div>
          <button onClick={openEditor} style={{ background: 'none', border: 'none', color: '#2dd4bf', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
            Edit
          </button>
        </div>

        {(connectProfile?.interestGroups?.length || 0) > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {connectProfile.interestGroups.map(groupId => {
              const group = INTERESTS_TAXONOMY[groupId];
              if (!group) return null;
              const Icon = group.icon;
              const groupSpecific = (connectProfile.interests || []).filter(i => group.interests.includes(i));

              return (
                <div key={groupId}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <Icon size={14} color={group.color} />
                    <span style={{ color: group.color, fontSize: '12px', fontWeight: 700 }}>{group.label}</span>
                    {groupSpecific.length > 0 && (
                      <span style={{ color: '#52525b', fontSize: '11px' }}>({groupSpecific.length})</span>
                    )}
                  </div>
                  {groupSpecific.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '20px' }}>
                      {groupSpecific.map(interest => (
                        <span key={interest} style={{
                          padding: '3px 8px', borderRadius: '10px', background: group.bg,
                          color: group.color, fontSize: '11px', fontWeight: 500
                        }}>
                          {interest}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ color: '#71717a', fontSize: '13px', marginBottom: '10px' }}>No interests set yet</p>
            <button onClick={openEditor} style={{ ...primaryButtonStyle, padding: '10px 20px', fontSize: '13px', display: 'inline-flex' }}>
              <Plus size={14} style={{ marginRight: '6px' }} /> Choose Your Interests
            </button>
          </div>
        )}
      </div>

      {/* Interest Tags (from Spark sync) */}
      {connectProfile?.interestTags?.length > 0 && (
        <div style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <Tag size={16} color="#fbbf24" />
            <span style={{ color: '#fafafa', fontSize: '14px', fontWeight: 600 }}>Your Tags</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {connectProfile.interestTags.slice(0, 20).map(tag => (
              <span key={tag} style={{ background: '#27272a', color: '#a1a1aa', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Location */}
      <div style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MapPin size={16} color="#22d3ee" />
          <span style={{ color: '#fafafa', fontSize: '14px', fontWeight: 600 }}>Location</span>
        </div>
        <p style={{ color: '#a1a1aa', fontSize: '13px', marginTop: '8px' }}>
          {connectProfile?.location?.city || 'Townsville'}, {connectProfile?.location?.state || 'QLD'}
          {connectProfile?.syncLocationFromSpark && <span style={{ color: '#71717a' }}> · Synced from Spark</span>}
        </p>
      </div>

      {/* App info */}
      <div style={{ textAlign: 'center', padding: '20px 0', color: '#3f3f46', fontSize: '12px' }}>
        <p>Connect by Narbodica · v1.0.0</p>
        <p style={{ marginTop: '4px' }}>Same account across all Narbodica apps</p>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, sublabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
      <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#18181b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={16} color="#2dd4bf" />
      </div>
      <div>
        <div style={{ color: '#fafafa', fontSize: '14px', fontWeight: 600 }}>{label}</div>
        {sublabel && <div style={{ color: '#71717a', fontSize: '12px', marginTop: '2px' }}>{sublabel}</div>}
      </div>
    </div>
  );
}

function StatBadge({ icon: Icon, value, label }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <Icon size={14} color="#71717a" style={{ marginBottom: '4px' }} />
      <div style={{ color: '#fafafa', fontSize: '16px', fontWeight: 700 }}>{value}</div>
      <div style={{ color: '#71717a', fontSize: '10px', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

function Toast({ message }) {
  return (
    <div style={{
      position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
      background: message.type === 'error' ? 'rgba(248,113,113,0.95)' : 'rgba(45,212,191,0.95)',
      color: '#000', padding: '12px 24px', borderRadius: '12px', fontWeight: 600, fontSize: '14px',
      zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', fontFamily: '"Inter", sans-serif'
    }}>
      {message.text}

      {/* FeedbackButton rendered in main app */}
    </div>
  );
}

// ============================================
// STYLES
// ============================================
const shellStyle = {
  minHeight: '100dvh',
  background: '#000',
  fontFamily: '"Inter", -apple-system, sans-serif',
  display: 'flex',
  justifyContent: 'center',
  padding: '0'
};

const containerStyle = {
  maxWidth: '440px',
  width: '100%',
  background: '#09090b',
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  borderLeft: '1px solid #18181b',
  borderRight: '1px solid #18181b'
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  background: '#18181b',
  border: '1px solid #27272a',
  borderRadius: '10px',
  color: '#fafafa',
  fontSize: '14px',
  fontFamily: '"Inter", sans-serif',
  outline: 'none',
  transition: 'border-color 0.2s'
};

const labelStyle = {
  display: 'block',
  color: '#a1a1aa',
  fontSize: '12px',
  fontWeight: 600,
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px'
};

const primaryButtonStyle = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)',
  border: 'none',
  borderRadius: '12px',
  color: '#000',
  fontWeight: 700,
  fontSize: '15px',
  cursor: 'pointer',
  fontFamily: '"Inter", sans-serif',
  transition: 'all 0.2s'
};

const iconBtnStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  background: '#18181b',
  border: 'none',
  color: '#a1a1aa',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s'
};

const globalStyles = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #27272a; border-radius: 2px; }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in { animation: fadeIn 0.3s ease-out; }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin { animation: spin 1s linear infinite; }

  input:focus, select:focus {
    border-color: #2dd4bf !important;
    box-shadow: 0 0 0 3px rgba(45,212,191,0.1) !important;
  }

  input[type="checkbox"] {
    accent-color: #2dd4bf;
  }

  /* Hide scrollbar on category pills */
  div::-webkit-scrollbar { display: none; }
`;